import { ScrollLock } from "./dialog";
import { Photo } from "./photo";
import {
  RecommendedLessons,
  RecommendedVideoCards,
  splitRecommendedLessons,
} from "./recommended-lessons";
import {
  ChatComposer,
  CommentBubble,
  CommunityHeader,
  CommunityTag,
  MetaAction,
  ShowAllButton,
} from "./community-ui";

// Comments the class shows before "Ver todos" (replies stay folded).
const COMMENT_PREVIEW = 4;
import { Select } from "./select";
import React, { useEffect, useRef, useState, type FormEvent } from "react";
import { createRoot } from "react-dom/client";
import {
  QueryClient,
  QueryClientProvider,
  queryOptions,
  useQueries,
  useQuery,
} from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { z } from "zod";
import {
  AlertCircle,
  Award,
  Bell,
  Bookmark,
  BookOpen,
  CalendarDays,
  Check,
  ChevronRight,
  CircleCheck,
  Clock3,
  Download,
  Flame,
  LayoutDashboard,
  LockKeyhole,
  LogOut,
  MailCheck,
  Menu,
  CreditCard,
  Star,
  MessageCircle,
  Reply,
  Map,
  Play,
  Settings,
  ShieldCheck,
  Sparkles,
  Users,
  Wrench,
  X,
} from "lucide-react";
import { AdminPanel } from "./admin-panel";
import { Avatar } from "./avatar";
import {
  CertificateProgress,
  ComingSoon,
  ContinueWatching,
  StartCourse,
} from "./overview-sections";
import { PasswordInput } from "./password-input";
import {
  CollapseToggle,
  sidebarLabel,
  useSidebarCollapsed,
} from "./sidebar-collapse";
import { cn } from "@hey404/ui";
import {
  Button,
  Doodle,
  HandArrow,
  Input,
  Marker,
  PageHeader,
  PaperNote,
  Ribbon,
  Sparkle,
  Squiggle,
  Sticker,
  WindowBar,
} from "./design-system";
import { CheckoutLaunchBar, CheckoutLayout } from "./checkout-layout";
import {
  checkoutLandingUrl,
  checkoutMessages as checkoutCopy,
  launchCouponCode,
} from "./checkout-content";
import { ToastProvider } from "./toast";
import {
  adminSections,
  adminSectionPaths,
  adminSectionFromPath,
  type AdminSection,
} from "./admin-operations";
import { MuxLessonPlayer } from "./mux-lesson-player";
import { VimeoLessonPlayer } from "./vimeo-lesson-player";
import { AccountSettings, SettingsLoading } from "./account-settings";
import { InterestsForm } from "./interests-form";
import { Markdown } from "./markdown";
import { GuestActivation, type GuestAccount } from "./checkout-activation";
import {
  AppShellSkeleton,
  ContinueSkeleton,
  PanelSkeleton,
  PlayerSkeleton,
} from "./loading";
import {
  btnPrimary,
  btnSecondary,
  fieldErrorClass,
  inputClass,
  kickerClass,
  btnAccent,
} from "./admin-ui";
import { LearningHub, LessonBookmark, LessonCompanion } from "./learning-tools";
import { SavedLessons } from "./saved-lessons";
import { BuyCourseGate } from "./buy-course-gate";
import { CourseForum } from "./course-forum";
import {
  apiFetch,
  apiUrl,
  authClient,
  clearAuthToken,
  downloadAuthenticatedFile,
  saveAuthToken,
} from "./lib/auth";
import { timeAgo } from "./lib/time";
import { trackInitiateCheckout, trackPurchase } from "./lib/meta-pixel";
import {
  identifyStudent,
  initAnalytics,
  resetAnalytics,
  track,
} from "./lib/analytics";
import "./styles.css";

initAnalytics();

if ("serviceWorker" in navigator)
  window.addEventListener("load", () =>
    navigator.serviceWorker.register("/sw.js").catch(() => undefined),
  );

// Amounts from the API are in minor units (cents). Format by currency.
const formatPrice = (amountMinor: number, currency: string) =>
  new Intl.NumberFormat("es", {
    style: "currency",
    currency,
    // Drop the ".00" on whole amounts, keep the cents when there are any (UNB2-152).
    minimumFractionDigits: amountMinor % 100 === 0 ? 0 : 2,
  }).format(amountMinor / 100);
type CheckoutCoupon = {
  code: string;
  percentOff: number;
  // Per course: list price, discount and what is charged (server-computed).
  lines: Array<{
    courseId: string;
    listAmountMinor: number;
    discountMinor: number;
    amountMinor: number;
  }>;
  subtotalMinor: number;
  discountMinor: number;
  totalMinor: number;
};
type CheckoutQuote = {
  items: Array<{
    courseId: string;
    slug: string;
    title: string;
    amountMinor: number;
  }>;
  total: number;
  currency: string;
  // With a discount code: the server-priced result, or why it doesn't apply.
  coupon?: CheckoutCoupon;
  couponError?: string;
  // Courses the user already owns: excluded from the total, shown as a notice.
  excluded?: Array<{ slug: string; title: string }>;
};
type NavigationContext = {
  avatarUrl?: string | null;
  counts: {
    courses: number;
    paths: number;
    events: number;
    notifications: number;
    unreadNotifications: number;
    teams: number;
    certificates: number;
  };
  visibility: Record<
    "paths" | "events" | "notifications" | "teams" | "coach" | "certificates",
    boolean
  >;
};
const go = (path: string) => {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
};
type ManagedCourse = {
  id: string;
  slug: string;
  title: string;
  description: string;
  subtitle?: string;
  category?: string;
  thumbnailUrl?: string | null;
  saleStatus?: "on_sale" | "coming_soon";
  enrolled?: boolean;
  basePriceMinor?: number;
  baseCurrency?: string;
  access?: "free" | "paid" | "subscription";
  forum: { enabled: boolean } | null;
  instructor?: {
    id: string;
    name: string;
    headline: string;
    bio: string;
    avatarUrl: string | null;
    websiteUrl: string | null;
  } | null;
  modules: Array<{
    id: string;
    title: string;
    position: number;
    lessons: Array<{
      id: string;
      title: string;
      content: string;
      durationMinutes: number;
      position: number;
      videoUrl: string | null;
      videoSource: "vimeo" | "external" | "mux" | null;
      thumbnailUrl?: string | null;
      kind?: "video" | "article" | "quiz" | "project";
      transcript?: string;
      captionsUrl?: string | null;
      locked?: boolean;
    }>;
  }>;
};
type PlayerModule = {
  number: string;
  title: string;
  progress: string;
  locked?: boolean;
  lessons: Array<{
    id: string;
    title: string;
    time: string;
    durationMinutes?: number;
    content?: string;
    videoUrl?: string | null;
    videoSource?: "vimeo" | "external" | "mux" | null;
    thumbnailUrl?: string | null;
    kind?: "video" | "article" | "quiz" | "project";
    captionsUrl?: string | null;
    locked?: boolean;
  }>;
};
type Certificate = {
  id: string;
  verificationCode: string;
  courseTitle: string;
  issuedAt: string;
};

const emailField = z
  .string()
  .trim()
  .min(1, "Ingresa tu email.")
  .email("Ingresa un email válido.")
  .transform((value) => value.toLowerCase());
const passwordField = z
  .string()
  .min(1, "Ingresa tu contraseña.")
  .min(10, "La contraseña debe tener al menos 10 caracteres.");
const loginSchema = z.object({
  email: emailField,
  password: passwordField,
});
const signupSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(2, "Ingresa tu nombre completo.")
      .max(80, "El nombre no puede superar los 80 caracteres."),
    email: emailField,
    password: passwordField.regex(
      /^(?=.*[A-Za-z])(?=.*\d)/,
      "Usa al menos una letra y un número.",
    ),
    confirmPassword: z.string().min(1, "Repite tu contraseña."),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ["confirmPassword"],
    message: "Las contraseñas no coinciden.",
  });

const authErrorMessage = (error: { code?: string; message?: string }) => {
  const code = error.code?.toUpperCase();
  if (code?.includes("INVALID_EMAIL_OR_PASSWORD"))
    return "El email o la contraseña no son correctos.";
  if (code?.includes("USER_ALREADY_EXISTS"))
    return "Ya existe una cuenta con ese email. Prueba ingresando.";
  if (code?.includes("TOO_MANY_REQUESTS"))
    return "Hiciste demasiados intentos. Espera unos minutos y vuelve a probar.";
  if (code?.includes("PASSWORD_TOO_SHORT"))
    return "La contraseña debe tener al menos 10 caracteres.";
  if (code?.includes("EMAIL_NOT_VERIFIED"))
    return "Verifica tu email antes de ingresar. Te enviamos un enlace al crear la cuenta.";
  if (error.message?.toLowerCase().includes("failed to fetch"))
    return "No pudimos conectar. Revisa tu conexión e intenta nuevamente.";
  return "No pudimos completar la operación. Intenta nuevamente.";
};

// Error notice for the auth/registration forms: the message, an "Inicia sesión"
// shortcut when the email is already taken (UNB2-149), and an always-present
// "Contactar soporte" out so the user is never stuck (UNB2-148).
// V30 callout for form-level errors: ink frame, danger bar on the left, ink
// text. The bar is a signal, never the only one: the message says what failed.
const authCalloutClass =
  "border-ink border-l-danger bg-paper border-surface border-l-8 p-4 text-sm";

function AuthErrorBox({
  message,
  emailInUse,
  loginHref,
}: {
  message: string;
  emailInUse?: boolean;
  loginHref: string;
}) {
  return (
    <div role="alert" aria-live="assertive" className={authCalloutClass}>
      <p className="flex items-start gap-2">
        <AlertCircle className="mt-0.5 shrink-0" size={17} aria-hidden="true" />
        <span>{message}</span>
      </p>
      <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 pl-6 font-mono text-sm">
        {emailInUse && (
          <a href={loginHref} className="font-bold underline">
            Inicia sesión →
          </a>
        )}
        <a href="mailto:hola@hey404.com" className="underline">
          ¿Necesitas ayuda? Contactar soporte
        </a>
      </p>
    </div>
  );
}

type NotificationItem = {
  id: string;
  kind?: "course" | "reply" | "review" | "event" | "billing" | "achievement";
  title: string;
  body: string;
  href: string | null;
  readAt: string | null;
  createdAt: string;
};

// The top-bar bell: opens a dropdown that lazy-loads the notifications (fetched
// only when opened, rendered in growing batches as you scroll, capped at 20).
// With more than 20 it offers a shortcut to the full /notifications page once
// the capped batch is loaded (UNB2-162 follow-up).
const NOTIF_CAP = 20;

// Each notification kind gets its own tile (icon + accent), so the list scans
// at a glance; unknown kinds fall back to the course look.
const notificationKinds = {
  course: { icon: BookOpen, tone: "bg-acid", label: "Curso" },
  reply: { icon: MessageCircle, tone: "bg-grape", label: "Respuesta" },
  review: { icon: Star, tone: "bg-lemon", label: "Reseña" },
  event: { icon: CalendarDays, tone: "bg-bubble", label: "Evento" },
  billing: { icon: CreditCard, tone: "bg-paper", label: "Pago" },
  achievement: { icon: Award, tone: "bg-lemon", label: "Logro" },
} as const;

function NotificationsBell({
  unread,
  go,
}: {
  unread: number;
  go: (href: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(8);
  const wrapRef = useRef<HTMLDivElement>(null);
  const query = useQuery({
    queryKey: ["student", "notifications", "bell"],
    queryFn: () =>
      apiFetch<{ notifications: NotificationItem[] }>(
        "/api/education/notifications",
      ),
    enabled: open,
  });
  const all = query.data?.notifications ?? [];
  useEffect(() => {
    if (open) setVisible(8);
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node))
        setOpen(false);
    };
    const onKey = (event: KeyboardEvent) =>
      event.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);
  async function markRead(id: string) {
    await apiFetch(`/api/education/notifications/${id}/read`, {
      method: "PATCH",
    });
    await queryClient.invalidateQueries({
      queryKey: ["student", "notifications", "bell"],
    });
    await queryClient.invalidateQueries({
      queryKey: ["student", "navigation"],
    });
  }
  const cap = Math.min(all.length, NOTIF_CAP);
  const shown = all.slice(0, Math.min(visible, NOTIF_CAP));
  const seeAll = all.length > NOTIF_CAP && visible >= NOTIF_CAP;
  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label={unread ? `Avisos, ${unread} sin leer` : "Avisos"}
        aria-expanded={open}
        aria-haspopup="dialog"
        className={cn(
          btnSecondary,
          "relative h-11 w-11 px-0",
          open && "bg-grape",
        )}
      >
        <Bell
          size={18}
          aria-hidden="true"
          className={unread ? "bell-ring" : undefined}
        />
        {unread > 0 && (
          <span
            aria-hidden="true"
            className="border-ink bg-bubble border-chip absolute -right-2.5 -top-2.5 grid h-6 min-w-6 place-items-center px-1 font-mono text-xs font-bold leading-none"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            role="dialog"
            aria-label="Avisos"
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.22, 0.8, 0.25, 1] }}
            style={{ transformOrigin: "top right" }}
            className="border-ink bg-paper shadow-brutal-lg border-surface absolute right-0 top-[calc(100%+12px)] z-40 w-[22rem] max-w-[calc(100vw-2rem)]"
          >
            <span
              aria-hidden="true"
              className="washi -top-3 left-8 z-20"
              style={{ rotate: "-5deg" }}
            />
            <div className="border-ink border-b-(length:--border-surface) bg-lemon flex items-center gap-3 px-4 py-3">
              <strong className="font-display text-xl font-extrabold">
                Avisos
              </strong>
              {unread > 0 && (
                <span className="border-ink bg-paper border-chip ml-auto rotate-[2deg] px-2 py-0.5 font-mono text-xs font-bold uppercase">
                  {unread} sin leer
                </span>
              )}
            </div>
            <div
              className="max-h-[26rem] overflow-y-auto overscroll-contain"
              onScroll={(event) => {
                const el = event.currentTarget;
                if (
                  el.scrollTop + el.clientHeight >= el.scrollHeight - 40 &&
                  visible < cap
                )
                  setVisible((value) => Math.min(value + 8, NOTIF_CAP));
              }}
            >
              {query.isPending ? (
                <div role="status" aria-label="Cargando avisos">
                  {[0, 1, 2].map((index) => (
                    <div
                      key={index}
                      className="border-ink/15 flex gap-3 border-b px-4 py-4"
                    >
                      <span className="loading-block bg-ink/10 block h-10 w-10 shrink-0" />
                      <span className="flex-1 space-y-2">
                        <span className="loading-block bg-ink/10 block h-4 w-3/4" />
                        <span className="loading-block bg-ink/10 block h-3 w-full" />
                      </span>
                    </div>
                  ))}
                </div>
              ) : shown.length === 0 ? (
                <div className="px-6 py-8 text-center">
                  <Doodle
                    name="face-lime"
                    float
                    tilt={6}
                    className="mx-auto w-16"
                  />
                  <p className="font-display mt-4 text-xl font-extrabold">
                    Todo al día
                  </p>
                  <p className="text-ink-soft mt-1 text-sm">
                    No tienes avisos por ahora.
                  </p>
                </div>
              ) : (
                <ul>
                  {shown.map((item) => {
                    const kind =
                      notificationKinds[item.kind ?? "course"] ??
                      notificationKinds.course;
                    const Icon = kind.icon;
                    const unreadItem = !item.readAt;
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={async () => {
                            if (unreadItem) await markRead(item.id);
                            if (item.href) {
                              setOpen(false);
                              go(item.href);
                            }
                          }}
                          className={`border-ink/15 hover:bg-bone flex w-full cursor-pointer items-start gap-3 border-b px-4 py-3.5 text-left transition-colors ${unreadItem ? "bg-bone" : ""}`}
                        >
                          <span
                            aria-hidden="true"
                            className={`border-ink border-chip grid h-10 w-10 shrink-0 place-items-center ${kind.tone} ${unreadItem ? "" : "opacity-60"}`}
                          >
                            <Icon size={18} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-2">
                              <span
                                className={`block text-sm leading-tight ${unreadItem ? "font-display font-extrabold" : "text-ink-soft"}`}
                              >
                                {item.title}
                              </span>
                              {unreadItem && (
                                <span className="bg-bubble border-ink ml-auto size-2.5 shrink-0 rounded-full border">
                                  <span className="sr-only">Sin leer</span>
                                </span>
                              )}
                            </span>
                            {item.body && (
                              <span className="text-ink-soft mt-1 block text-xs leading-snug">
                                {item.body}
                              </span>
                            )}
                            <span className="text-ink-soft mt-1.5 block font-mono text-xs">
                              {kind.label} · {timeAgo(item.createdAt)}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            {seeAll && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  go("/notifications");
                }}
                className={cn(
                  btnAccent,
                  "w-full justify-between rounded-none border-x-0 border-b-0 px-4 py-3 text-sm shadow-none",
                )}
              >
                Ver todas las notificaciones <span aria-hidden="true">↗</span>
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// The email awaiting verification, held at module scope so it survives the auth
// route remounting. better-auth's post-sign-up session refetch flips
// useSession().isPending to true (data is null while logged out), which unmounts
// the auth screen via the root skeleton and would reset a component-local
// useState — the notice would vanish. A real page reload clears this.
let pendingVerifyEmail: string | null = null;

// Shown after a password sign-up (or a sign-in blocked by EMAIL_NOT_VERIFIED):
// the account exists but stays locked until the emailed link is clicked. Offers
// a resend so a lost email isn't a dead end. `callbackURL` is where the verify
// link lands the user after auto sign-in (back to where they were headed).
function VerifyEmailNotice({
  email,
  callbackURL,
  onBack,
}: {
  email: string;
  callbackURL: string;
  onBack?: () => void;
}) {
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">(
    "idle",
  );
  // Signing up with an email that already has an (unverified) account lands here
  // too — offer a login shortcut without revealing the email exists (no
  // enumeration oracle; see UNB2-149 / UNB2-77).
  const returnTo = new URL(callbackURL, window.location.origin);
  const loginHref = `/login?next=${encodeURIComponent(
    returnTo.pathname + returnTo.search,
  )}`;
  async function resend() {
    setStatus("sending");
    const { error } = await authClient.sendVerificationEmail({
      email,
      callbackURL,
    });
    setStatus(error ? "error" : "sent");
  }
  return (
    <div>
      <span className="border-ink bg-lemon border-chip grid h-14 w-14 place-items-center">
        <MailCheck size={28} />
      </span>
      <h2 className="font-display mt-6 text-4xl font-extrabold sm:text-5xl">
        Verifica tu email
      </h2>
      <p className="text-ink-soft mt-3 text-sm leading-relaxed">
        Te enviamos un enlace a <strong className="text-ink">{email}</strong>.
        Ábrelo para activar tu cuenta; después vas a poder ingresar normalmente.
      </p>
      <Button
        variant="secondary"
        full
        onClick={resend}
        disabled={status === "sending" || status === "sent"}
        className="mt-8"
      >
        {status === "sent"
          ? "Enlace reenviado ✓"
          : status === "sending"
            ? "Enviando…"
            : "Reenviar enlace"}
      </Button>
      {status === "error" && (
        <FieldMessage>
          No pudimos reenviar el enlace. Prueba de nuevo en un momento.
        </FieldMessage>
      )}
      <p className="text-ink-soft mt-4 text-center text-sm">
        ¿No lo ves? Revisa spam o promociones.
      </p>
      <p className="text-ink-soft mt-2 text-center text-sm">
        ¿Este email ya es tuyo?{" "}
        <a href={loginHref} className="text-ink font-bold underline">
          Inicia sesión
        </a>
      </p>
      {onBack && (
        <p className="mt-6 text-center text-sm">
          <button type="button" onClick={onBack} className={authLinkClass}>
            ← Volver
          </button>
        </p>
      )}
    </div>
  );
}

// The brand side is a collage on a pink V30 surface: copy sits on a taped
// cream note (ink on cream, not ink straight on pink), the three promises are
// tilted stickers, and the doodles (flower as protagonist) stay on the edges.
function BrandPanel() {
  return (
    <div className="border-ink bg-bubble platform-grid border-b-(length:--border-surface) lg:border-r-(length:--border-surface) relative flex flex-col overflow-hidden lg:border-b-0">
      <Doodle
        name="flower-yellow"
        float
        tilt={-8}
        className="absolute -right-6 -top-6 w-28 lg:-right-8 lg:w-40"
      />
      <Sparkle className="text-lemon absolute left-[46%] top-8 w-7" />
      <Sparkle
        delay={1.4}
        className="text-paper absolute right-10 top-44 hidden w-5 lg:block"
      />
      <div className="relative flex-1 p-8 lg:p-12">
        <a
          href="https://hey404.com"
          className="bg-paper border-ink border-chip shadow-brutal inline-block rotate-[-2deg] px-3 py-2"
        >
          <img src="/logo-final.svg" alt="hey404" className="h-10 w-auto" />
        </a>
        <PaperNote tilt={-1.5} tape="acid" className="mt-10 max-w-md">
          <h1 className="font-display text-4xl font-extrabold lg:text-5xl">
            Tu aprendizaje, en <Marker>un solo lugar.</Marker>
          </h1>
          <p className="text-ink-soft mt-4 text-lg">
            Cursos, avances, entregas y certificados.
          </p>
        </PaperNote>
        <div className="relative mt-8 flex items-start gap-2">
          <ul className="flex flex-col items-start gap-3 font-mono text-sm">
            <li>
              <Sticker tone="paper" tilt={-2} className="normal-case">
                01 · Continúa donde dejaste
              </Sticker>
            </li>
            <li>
              <Sticker tone="lemon" tilt={1.5} className="normal-case">
                02 · Entrega tu proyecto
              </Sticker>
            </li>
            <li>
              <Sticker tone="grape" tilt={-1} className="normal-case">
                03 · Obtén tu certificado
              </Sticker>
            </li>
          </ul>
          <HandArrow
            delay={500}
            className="text-ink -mt-6 hidden w-24 shrink-0 sm:block"
          />
        </div>
        <Doodle
          name="face-lime"
          float
          delay={1.2}
          tilt={6}
          className="absolute bottom-4 right-6 hidden w-24 lg:block"
        />
      </div>
      <Ribbon
        items={["Aprende", "Crea", "Publica", "Repite"]}
        className="relative -mx-4 mb-6"
      />
    </div>
  );
}

// Every way into the platform (login, signup, email verification, password
// reset) shares this frame, so they can't drift apart visually. The card is
// the landing's browser window on cream paper, taped to the page, with a few
// pieces scattered outside the frame on wide screens.
function AuthFrame({ children }: { children: React.ReactNode }) {
  return (
    <main className="platform-grid grid min-h-dvh place-items-center overflow-hidden px-5 py-14">
      <div className="relative w-full max-w-5xl">
        <Sparkle className="text-acid absolute -left-14 top-24 hidden w-10 xl:block" />
        <Doodle
          name="question-yellow"
          float
          delay={0.8}
          tilt={10}
          className="absolute -right-20 top-1/3 hidden w-14 xl:block"
        />
        <Doodle
          name="cursor-cream"
          tilt={-12}
          className="absolute -bottom-10 -left-8 z-10 hidden w-16 lg:block"
        />
        <div className="border-ink bg-paper border-surface shadow-brutal-lg overflow-hidden">
          <WindowBar tone="lemon" title="app.hey404.com" />
          <div className="grid lg:grid-cols-2">
            <BrandPanel />
            <div className="ruled-paper relative p-7 sm:p-12">{children}</div>
          </div>
        </div>
      </div>
    </main>
  );
}

function AuthHeading({ kicker, title }: { kicker: string; title: string }) {
  // The last word gets the hand-drawn underline, like the references' circled
  // and underlined words.
  const words = title.split(" ");
  const last = words.pop();
  return (
    <>
      <Sticker tone="acid">{kicker}</Sticker>
      <h2 className="font-display mt-4 text-4xl font-extrabold sm:text-5xl">
        {words.join(" ")} <Squiggle>{last}</Squiggle>
      </h2>
    </>
  );
}

// V30 `h4-link`: underline that thickens on hover, with a 44px touch target.
const authLinkClass =
  "inline-flex min-h-11 items-center underline decoration-2 underline-offset-[0.2em] hover:decoration-[3px]";

// Inline error under a field (V30 `h4-field__error`).
function FieldMessage({
  id,
  children,
}: {
  id?: string;
  children: React.ReactNode;
}) {
  return (
    <span id={id} className={cn(fieldErrorClass, "block")}>
      {children}
    </span>
  );
}

// Label, control and error for one auth field (V30 `h4-field`). The label
// wraps the control so clicking the text focuses it.
function AuthField({
  label,
  error,
  errorId,
  children,
}: {
  label: string;
  error?: string;
  errorId?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="grid gap-2">
      <span className="font-mono text-base font-bold leading-snug">
        {label}
      </span>
      {children}
      {error && <FieldMessage id={errorId}>{error}</FieldMessage>}
    </label>
  );
}

// Sends /signup to the checkout of the course on sale, so there is a single
// way in: pay with your email.
function SignupToCheckout() {
  useEffect(() => {
    let active = true;
    apiFetch<{
      courses: Array<{ slug: string; access?: string; saleStatus?: string }>;
    }>("/api/courses")
      .then((data) => {
        if (!active) return;
        const onSale = data.courses.find(
          (course) =>
            course.access === "paid" && course.saleStatus !== "coming_soon",
        );
        window.location.replace(
          onSale
            ? `/checkout?courses=${encodeURIComponent(onSale.slug)}`
            : checkoutLandingUrl,
        );
      })
      .catch(() => {
        if (active) window.location.replace(checkoutLandingUrl);
      });
    return () => {
      active = false;
    };
  }, []);
  return (
    <CheckoutLayout
      sessionPending
      backHref={checkoutLandingUrl}
      summary={
        <p className="checkout-status" role="status">
          {checkoutCopy.quotePending}
        </p>
      }
    >
      {null}
    </CheckoutLayout>
  );
}

function AuthScreen({ mode }: { mode: "login" | "signup" }) {
  const signup = mode === "signup";
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Set when signup fails because the email already has an account (UNB2-149).
  const [emailInUse, setEmailInUse] = useState(false);
  // Set once a password account exists but is still unverified — swaps the form
  // for the "check your email" notice (sign-up, or a login blocked as unverified).
  // Mirrored to module scope so it survives the remount described above.
  const [pendingEmail, setPendingEmailState] = useState<string | null>(
    () => pendingVerifyEmail,
  );
  const setPendingEmail = (value: string | null) => {
    pendingVerifyEmail = value;
    setPendingEmailState(value);
  };
  const clearFieldError = (field: string) => {
    if (!fieldErrors[field]) return;
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
  };
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    setError("");
    setEmailInUse(false);
    setFieldErrors({});
    const data = new FormData(event.currentTarget);
    const values = Object.fromEntries(data.entries());
    const validation = signup
      ? signupSchema.safeParse(values)
      : loginSchema.safeParse(values);
    if (!validation.success) {
      const nextErrors: Record<string, string> = {};
      validation.error.issues.forEach((issue) => {
        const field = String(issue.path[0] ?? "form");
        if (!nextErrors[field]) nextErrors[field] = issue.message;
      });
      setFieldErrors(nextErrors);
      setError("Revisa los campos marcados para continuar.");
      window.requestAnimationFrame(() => {
        form.querySelector<HTMLElement>("[aria-invalid='true']")?.focus();
      });
      return;
    }
    setLoading(true);
    const next = new URLSearchParams(window.location.search).get("next") || "/";
    // Absolute app-origin URL the verification link returns to after auto
    // sign-in — so the user lands back where they were headed, not on the API.
    const callbackURL = new URL(next, window.location.origin).toString();
    try {
      if (signup) {
        const { name, email, password } = signupSchema.parse(values);
        const result = await authClient.signUp.email({
          name,
          email,
          password,
          callbackURL,
        });
        if (result.error) {
          setError(authErrorMessage(result.error));
          setEmailInUse(
            Boolean(
              result.error.code?.toUpperCase().includes("USER_ALREADY_EXISTS"),
            ),
          );
          setLoading(false);
          return;
        }
        // With requireEmailVerification the sign-up creates the account but
        // returns no session (token is null): the user must confirm their email
        // before they can sign in. Show the "check your email" notice.
        if (!result.data?.token) {
          setPendingEmail(email);
          setLoading(false);
          return;
        }
        saveAuthToken(result.data.token);
        window.location.assign(next);
        return;
      }
      const credentials = loginSchema.parse(values);
      const result = await authClient.signIn.email(credentials);
      if (result.error) {
        // Unverified account: steer to the same "check your email" notice (with
        // a resend) instead of a dead-end error.
        if (result.error.code === "EMAIL_NOT_VERIFIED") {
          setPendingEmail(credentials.email);
          setLoading(false);
          return;
        }
        setError(authErrorMessage(result.error));
        setLoading(false);
        return;
      }
      saveAuthToken(result.data?.token);
      window.location.assign(next);
    } catch (cause) {
      setError(
        authErrorMessage({
          message: cause instanceof Error ? cause.message : undefined,
        }),
      );
      setLoading(false);
    }
  }
  async function signInWithGoogle() {
    setError("");
    setLoading(true);
    const next = new URLSearchParams(window.location.search).get("next") || "/";
    const { error: googleError } = await authClient.signIn.social({
      provider: "google",
      // Absolute URL on the app's own origin. A relative callback gets resolved
      // against the API origin (api.hey404.com), sending the user to the API
      // root after login instead of back to the app (app.hey404.com).
      callbackURL: new URL(next, window.location.origin).toString(),
    });
    // On success better-auth redirects the browser to Google; only handle errors.
    if (googleError) {
      setError(authErrorMessage(googleError));
      setLoading(false);
    }
  }
  if (pendingEmail) {
    return (
      <AuthFrame>
        <VerifyEmailNotice
          email={pendingEmail}
          callbackURL={new URL(
            new URLSearchParams(window.location.search).get("next") || "/",
            window.location.origin,
          ).toString()}
          onBack={() => setPendingEmail(null)}
        />
      </AuthFrame>
    );
  }
  return (
    <AuthFrame>
      <AuthHeading
        kicker={signup ? "Crear cuenta" : "Bienvenido de nuevo"}
        title={signup ? "Crea tu acceso" : "Ingresa a hey404"}
      />
      <p className="text-ink-soft mt-3 text-sm">
        {signup
          ? "Usa el mismo email con el que compraste tus cursos."
          : "Accede a tus cursos, entregas y certificados."}
      </p>
      <Button
        variant="secondary"
        full
        onClick={signInWithGoogle}
        disabled={loading}
        // Grape (the shared hover) washes out Google's four-colour mark; white
        // keeps it at full strength, and the lift still signals the hover.
        className="hover:bg-bone focus-visible:bg-bone hover:shadow-brutal-lg mt-8 gap-3 transition-[background-color,box-shadow,translate] hover:-translate-y-0.5"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="#4285F4"
            d="M23.52 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.57-5.17 3.57-8.87z"
          />
          <path
            fill="#34A853"
            d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.76-2.11-6.7-4.94H1.29v3.09A12 12 0 0 0 12 24z"
          />
          <path
            fill="#FBBC05"
            d="M5.3 14.31a7.2 7.2 0 0 1 0-4.62V6.6H1.29a12 12 0 0 0 0 10.8l4.01-3.09z"
          />
          <path
            fill="#EA4335"
            d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.29 6.6l4.01 3.09C6.24 6.86 8.88 4.75 12 4.75z"
          />
        </svg>
        Continuar con Google
      </Button>
      <div className="mt-6 flex items-center gap-3">
        <span className="bg-ink h-0.5 flex-1 opacity-20" aria-hidden="true" />
        <span className={cn(kickerClass, "text-ink-soft text-xs")}>
          o con tu email
        </span>
        <span className="bg-ink h-0.5 flex-1 opacity-20" aria-hidden="true" />
      </div>
      <form className="mt-6 space-y-5" onSubmit={submit} noValidate>
        {signup && (
          <AuthField
            label="Nombre"
            error={fieldErrors.name}
            errorId="name-error"
          >
            <Input
              name="name"
              autoComplete="name"
              required
              aria-invalid={Boolean(fieldErrors.name)}
              aria-describedby={fieldErrors.name ? "name-error" : undefined}
              onChange={() => clearFieldError("name")}
            />
          </AuthField>
        )}
        <AuthField
          label="Email"
          error={fieldErrors.email}
          errorId="email-error"
        >
          <Input
            name="email"
            type="email"
            autoComplete="email"
            required
            aria-invalid={Boolean(fieldErrors.email)}
            aria-describedby={fieldErrors.email ? "email-error" : undefined}
            onChange={() => clearFieldError("email")}
          />
        </AuthField>
        <AuthField
          label="Contraseña"
          error={fieldErrors.password}
          errorId="password-error"
        >
          <PasswordInput
            name="password"
            autoComplete={signup ? "new-password" : "current-password"}
            minLength={10}
            required
            aria-invalid={Boolean(fieldErrors.password)}
            aria-describedby={
              fieldErrors.password ? "password-error" : undefined
            }
            onChange={() => clearFieldError("password")}
          />
        </AuthField>
        {!signup && (
          // Right under its field, where people look when the password fails.
          <p className="-mt-3 text-right text-sm">
            <button
              type="button"
              className={authLinkClass}
              onClick={() => go("/forgot-password")}
            >
              ¿Olvidaste tu contraseña?
            </button>
          </p>
        )}
        {signup && (
          <AuthField
            label="Repetir contraseña"
            error={fieldErrors.confirmPassword}
            errorId="confirm-password-error"
          >
            <PasswordInput
              name="confirmPassword"
              autoComplete="new-password"
              required
              aria-invalid={Boolean(fieldErrors.confirmPassword)}
              aria-describedby={
                fieldErrors.confirmPassword
                  ? "confirm-password-error"
                  : undefined
              }
              onChange={() => clearFieldError("confirmPassword")}
            />
          </AuthField>
        )}
        {error && (
          <AuthErrorBox
            message={error}
            emailInUse={emailInUse}
            loginHref={`/login${window.location.search}`}
          />
        )}
        <Button type="submit" full disabled={loading} aria-busy={loading}>
          {loading ? "Un momento…" : signup ? "Crear mi cuenta" : "Ingresar"}
        </Button>
      </form>
      <p className="mt-5 text-sm">
        {signup ? "¿Ya tienes cuenta?" : "¿Todavía no tienes cuenta?"}{" "}
        <button
          type="button"
          className={cn(authLinkClass, "font-display font-extrabold")}
          onClick={() => go(signup ? "/login" : "/signup")}
        >
          {signup ? "Ingresa" : "Regístrate"}
        </button>
      </p>
    </AuthFrame>
  );
}

// One reply control, one look: uppercase mono at a legible size with a ≥24px
// tap target (padding + negative margin keeps it visually aligned). Shared by
// top-level comments and nested replies so the two can't drift apart again.
function AuthShell({
  kicker,
  title,
  children,
}: {
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <AuthFrame>
      <AuthHeading kicker={kicker} title={title} />
      {children}
    </AuthFrame>
  );
}

function ForgotPassword() {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setLoading(true);
    const email = String(new FormData(event.currentTarget).get("email"));
    try {
      // Same response whether or not the account exists (no user enumeration).
      await authClient.requestPasswordReset({
        email,
        redirectTo: `${window.location.origin}/reset-password`,
      });
      setSent(true);
    } catch {
      setError("No pudimos procesar el pedido. Prueba de nuevo en un momento.");
    } finally {
      setLoading(false);
    }
  }
  return (
    <AuthShell kicker="Recuperar acceso" title="¿Olvidaste tu contraseña?">
      {sent ? (
        <div className="mt-6">
          <p className="border-ink bg-acid border-surface flex items-start gap-2 p-4 text-sm">
            <CircleCheck className="mt-0.5 shrink-0" size={18} />
            <span>
              Si esa cuenta existe, te enviamos un enlace para restablecer tu
              contraseña. Revisa tu correo (y el spam).
            </span>
          </p>
          <button
            type="button"
            onClick={() => go("/login")}
            className={cn(authLinkClass, "mt-4 text-sm")}
          >
            ← Volver a ingresar
          </button>
        </div>
      ) : (
        <>
          <p className="text-ink-soft mt-3 text-sm">
            Ingresa tu email y te enviamos un enlace para crear una contraseña
            nueva.
          </p>
          <form className="mt-8 space-y-5" onSubmit={submit} noValidate>
            <AuthField label="Email">
              <Input name="email" type="email" autoComplete="email" required />
            </AuthField>
            {error && (
              <p
                role="alert"
                className={cn(authCalloutClass, "flex items-start gap-2")}
              >
                <AlertCircle className="mt-0.5 shrink-0" size={17} />
                <span>{error}</span>
              </p>
            )}
            <Button type="submit" full disabled={loading} aria-busy={loading}>
              {loading ? "Un momento…" : "Enviarme el enlace"}
            </Button>
          </form>
          <button
            type="button"
            onClick={() => go("/login")}
            className={cn(authLinkClass, "mt-4 text-sm")}
          >
            ← Volver a ingresar
          </button>
        </>
      )}
    </AuthShell>
  );
}

function ResetPassword() {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    const data = new FormData(event.currentTarget);
    const password = String(data.get("password"));
    const confirm = String(data.get("confirmPassword"));
    if (password.length < 10) {
      setError("La contraseña debe tener al menos 10 caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setLoading(true);
    const result = await authClient.resetPassword({
      newPassword: password,
      token,
    });
    if (result.error) {
      setError(
        "El enlace es inválido o venció. Pide uno nuevo desde “¿Olvidaste tu contraseña?”.",
      );
      setLoading(false);
      return;
    }
    setDone(true);
  }
  if (!token)
    return (
      <AuthShell kicker="Restablecer contraseña" title="Enlace inválido">
        <p className="text-ink-soft mt-3 text-sm">
          Este enlace no es válido. Pide uno nuevo para continuar.
        </p>
        <button
          type="button"
          onClick={() => go("/forgot-password")}
          className={`${btnPrimary} mt-7 w-full cursor-pointer px-5 py-4 text-lg`}
        >
          Pedir un enlace nuevo
        </button>
      </AuthShell>
    );
  return (
    <AuthShell kicker="Restablecer contraseña" title="Crea tu nueva contraseña">
      {done ? (
        <div className="mt-6">
          <p className="border-ink bg-acid border-surface flex items-start gap-2 p-4 text-sm">
            <CircleCheck className="mt-0.5 shrink-0" size={18} />
            <span>
              Tu contraseña se actualizó. Ya puedes ingresar con ella.
            </span>
          </p>
          <button
            type="button"
            onClick={() => go("/login")}
            className={`${btnPrimary} mt-6 w-full cursor-pointer px-5 py-4 text-lg`}
          >
            Ir a ingresar
          </button>
        </div>
      ) : (
        <form className="mt-8 space-y-5" onSubmit={submit} noValidate>
          <AuthField label="Nueva contraseña">
            <PasswordInput
              name="password"
              autoComplete="new-password"
              minLength={10}
              required
            />
          </AuthField>
          <AuthField label="Repetir contraseña">
            <PasswordInput
              name="confirmPassword"
              autoComplete="new-password"
              minLength={10}
              required
            />
          </AuthField>
          {error && (
            <p
              role="alert"
              className={cn(authCalloutClass, "flex items-start gap-2")}
            >
              <AlertCircle className="mt-0.5 shrink-0" size={17} />
              <span>{error}</span>
            </p>
          )}
          <Button type="submit" full disabled={loading} aria-busy={loading}>
            {loading ? "Guardando…" : "Guardar contraseña"}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

function PlatformHeader({ user }: { user?: { email: string } }) {
  return (
    <header className="border-ink bg-paper border-b-(length:--border-surface) sticky top-0 z-20 flex h-16 items-center px-5">
      <button onClick={() => go("/")} aria-label="Ir al overview">
        <img src="/logo-final.svg" alt="hey404" className="h-10 w-auto" />
      </button>
      <div className="ml-auto flex items-center gap-3">
        {user ? (
          <>
            <span className="hidden font-mono text-xs sm:block">
              {user.email}
            </span>
            <button
              onClick={async () => {
                await authClient.signOut();
                clearAuthToken();
                window.location.assign("/login");
              }}
              className="border-ink bg-bone shadow-brutal border-chip p-2"
              aria-label="Cerrar sesión"
            >
              <LogOut size={17} />
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => go("/login")}
              className="font-display text-sm font-bold"
            >
              Ingresar
            </button>
            <button
              onClick={() => go("/signup")}
              className={`${btnPrimary} px-3 py-2 text-sm`}
            >
              Crear cuenta
            </button>
          </>
        )}
      </div>
    </header>
  );
}

function Checkout({ user }: { user?: { email: string } }) {
  const [loading, setLoading] = useState(false);
  const [changingAccount, setChangingAccount] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Set when the guest's email already owns everything in the cart.
  const [ownedBySignIn, setOwnedBySignIn] = useState(false);
  const clearFieldError = (field: string) => {
    if (!fieldErrors[field]) return;
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
  };
  // Guests pay with just an email; the account is created when
  // the payment is confirmed and activated with a code sent to that email.
  const [guestEmail, setGuestEmail] = useState("");
  const [quote, setQuote] = useState<CheckoutQuote | null>(null);
  const [quoteError, setQuoteError] = useState("");
  const [loadingQuote, setLoadingQuote] = useState(true);
  // Course blurb + lesson count per slug, pulled from the public catalog so the
  // cart can sell each course instead of just listing it (UNB2-153).
  const [courseInfo, setCourseInfo] = useState<
    Record<string, { subtitle: string; lessons: number }>
  >({});
  // A discount code (a campaign coupon or a personal perk). The server prices
  // the cart with it, course by course, so the summary shows exactly what will
  // be charged even when the code only covers some courses. Its use is
  // reserved when the order is created and spent when the payment lands.
  const [discountInput, setDiscountInput] = useState("");
  const [discount, setDiscount] = useState<CheckoutCoupon | null>(null);
  const [discountError, setDiscountError] = useState("");
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const payableTotal = discount?.totalMinor ?? quote?.total ?? 0;
  const discountAmount = discount?.discountMinor ?? 0;
  const discountLine = (courseId: string) =>
    discount?.lines.find((line) => line.courseId === courseId);
  // Some courses outside the code's promotion still pay full price.
  const partialDiscount = Boolean(
    discount?.lines.some((line) => line.discountMinor === 0),
  );
  const freeOrder = Boolean(discount) && payableTotal === 0;
  const [slugs] = useState(() =>
    (new URLSearchParams(window.location.search).get("courses") || "")
      .split(",")
      .filter(Boolean),
  );
  const loginPath = `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`;
  useEffect(() => {
    if (!slugs.length) {
      setLoadingQuote(false);
      return;
    }
    let active = true;
    apiFetch<CheckoutQuote>("/api/checkout/quote", {
      method: "POST",
      // Currency is decided server-side (single source of truth); the quote
      // response carries the currency the total is shown and charged in.
      body: JSON.stringify({ courses: slugs }),
    })
      .then((data) => {
        if (!active) return;
        setQuote(data);
        if (data.items.length) {
          trackInitiateCheckout(
            { amountMinor: data.total, currency: data.currency },
            data.items.map((item) => item.slug),
          );
          track("checkout_viewed", {
            courses: data.items.map((item) => item.slug),
            total: data.total / 100,
            currency: data.currency,
          });
        }
      })
      .catch(() => {
        if (active) setQuoteError(checkoutCopy.quoteError);
      })
      .finally(() => {
        if (active) setLoadingQuote(false);
      });
    return () => {
      active = false;
    };
  }, [slugs]);
  // Enrich the cart with each course's blurb + lesson count from the catalog.
  useEffect(() => {
    if (!slugs.length) return;
    let active = true;
    apiFetch<{
      courses: Array<{
        slug: string;
        subtitle?: string;
        description?: string;
        modules?: Array<{ lessons?: unknown[] }>;
      }>;
    }>("/api/courses")
      .then((data) => {
        if (!active) return;
        const map: Record<string, { subtitle: string; lessons: number }> = {};
        for (const course of data.courses)
          map[course.slug] = {
            subtitle: course.subtitle || course.description || "",
            lessons: (course.modules ?? []).reduce(
              (count, module) => count + (module.lessons?.length ?? 0),
              0,
            ),
          };
        setCourseInfo(map);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [slugs]);
  async function applyDiscount(codeToApply = discountInput) {
    const code = codeToApply.trim();
    if (!code) return;
    if (applyingDiscount || loading || loadingQuote || !quote?.items.length)
      return;
    setApplyingDiscount(true);
    setDiscountError("");
    try {
      const email = guestEmail.trim();
      const res = await apiFetch<CheckoutQuote>("/api/checkout/quote", {
        method: "POST",
        body: JSON.stringify({
          courses: slugs,
          discountCode: code,
          // Lets per-person rules (one use per buyer, first purchase) answer
          // before paying; the order checks them again either way.
          ...(!user && z.string().email().safeParse(email).success
            ? { email }
            : {}),
        }),
      });
      if (res.coupon) {
        setDiscount(res.coupon);
        track("discount_code_applied", { percent_off: res.coupon.percentOff });
      } else {
        setDiscount(null);
        setDiscountError(
          checkoutCopy.codeReasons[res.couponError ?? ""] ??
            checkoutCopy.codeInvalid,
        );
      }
    } catch {
      setDiscountError(checkoutCopy.codeError);
    } finally {
      setApplyingDiscount(false);
    }
  }
  // A campaign link (?cupon=CODE) applies its code as soon as the cart is
  // priced, once; the buyer can still remove it.
  const campaignCode = useRef(
    new URLSearchParams(window.location.search).get("cupon")?.trim() ?? "",
  );
  useEffect(() => {
    if (!campaignCode.current || !quote?.items.length) return;
    const code = campaignCode.current;
    campaignCode.current = "";
    setDiscountInput(code);
    void applyDiscount(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once when the quote arrives
  }, [quote]);
  function removeDiscount() {
    setDiscount(null);
    setDiscountInput("");
    setDiscountError("");
  }
  async function changeCheckoutAccount() {
    if (loading || changingAccount) return;
    setChangingAccount(true);
    setError("");
    try {
      const result = await authClient.signOut();
      if (result.error) throw new Error("Checkout sign-out failed");
      clearAuthToken();
      window.location.assign(loginPath);
    } catch {
      setError(checkoutCopy.signOutError);
      setChangingAccount(false);
    }
  }
  async function pay() {
    if (
      loading ||
      changingAccount ||
      loadingQuote ||
      applyingDiscount ||
      !quote?.items.length
    )
      return;
    setLoading(true);
    setError("");
    setOwnedBySignIn(false);
    try {
      const { redirectUrl } = await apiFetch<{ redirectUrl: string }>(
        "/api/checkout/orders",
        {
          method: "POST",
          body: JSON.stringify({
            courses: slugs,
            ...(discount ? { discountCode: discount.code } : {}),
            ...(user ? {} : { email: guestEmail.trim() }),
          }),
        },
      );
      track("checkout_payment_started", {
        courses: slugs,
        total: payableTotal / 100,
        currency: quote.currency,
        discount_percent: discount?.percentOff ?? 0,
        signed_in: Boolean(user),
      });
      window.location.href = redirectUrl;
    } catch (err) {
      // A code that was valid when applied could be used/revoked by pay time —
      // surface that against the code so the user can drop it and continue.
      const body = (err as { body?: { error?: string; reason?: string } }).body;
      if (body?.error === "invalid_discount_code") {
        setDiscount(null);
        setDiscountError(
          checkoutCopy.codeReasons[body.reason ?? ""] ??
            checkoutCopy.codeExpired,
        );
      } else if (body?.error === "already_owned_sign_in") {
        setOwnedBySignIn(true);
        setError(checkoutCopy.emailOwnsCourse);
      } else {
        setError(checkoutCopy.paymentError);
      }
      setLoading(false);
    }
  }
  function payAsGuest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const email = guestEmail.trim();
    const nextErrors: Record<string, string> = {};
    if (!z.string().email().safeParse(email).success)
      nextErrors.email = checkoutCopy.emailInvalid;
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      window.requestAnimationFrame(() => {
        form.querySelector<HTMLElement>("[aria-invalid='true']")?.focus();
      });
      return;
    }
    void pay();
  }
  const summary = (
    <>
      {loadingQuote ? (
        <p className="checkout-status" role="status">
          {checkoutCopy.quotePending}
        </p>
      ) : quoteError ? (
        <div className="checkout-status" role="alert">
          <strong>{quoteError}</strong>
          <p>{checkoutCopy.quoteRecovery}</p>
          <a
            className="checkout-button checkout-button-secondary"
            href={checkoutLandingUrl}
          >
            {checkoutCopy.catalog}
          </a>
        </div>
      ) : quote && quote.items.length ? (
        <>
          <div className="checkout-course-list">
            {quote.items.map((item, index) => (
              <article key={item.slug} className="checkout-course-item">
                <div className="checkout-course-art" aria-hidden="true">
                  <img
                    src={
                      [
                        "/checkout-brand/cursor-pink.svg",
                        "/checkout-brand/key-lavender.svg",
                        "/checkout-brand/face-lime.svg",
                      ][index % 3]
                    }
                    alt=""
                    width="56"
                    height="56"
                  />
                </div>
                <div className="checkout-course-meta">
                  <h3>{item.title}</h3>
                  {courseInfo[item.slug]?.subtitle && (
                    <p>{courseInfo[item.slug]?.subtitle}</p>
                  )}
                  <small>
                    {courseInfo[item.slug]?.lessons
                      ? checkoutCopy.lessons(
                          courseInfo[item.slug]?.lessons ?? 0,
                        )
                      : checkoutCopy.access}
                  </small>
                </div>
                <strong className="checkout-course-price">
                  {discountLine(item.courseId)?.discountMinor ? (
                    <>
                      <s className="checkout-price-was">
                        <span className="sr-only">
                          {checkoutCopy.priceWas}{" "}
                        </span>
                        {formatPrice(item.amountMinor, quote.currency)}
                      </s>
                      {formatPrice(
                        discountLine(item.courseId)?.amountMinor ?? 0,
                        quote.currency,
                      )}
                    </>
                  ) : (
                    formatPrice(item.amountMinor, quote.currency)
                  )}
                </strong>
              </article>
            ))}
          </div>
          {Boolean(quote.excluded?.length) && (
            <div className="checkout-note">
              <CircleCheck size={18} aria-hidden="true" />
              <p>
                <strong>
                  {quote.excluded?.map((item) => item.title).join(", ")}
                </strong>
                <br />
                {checkoutCopy.ownedBody}
              </p>
            </div>
          )}
          <div className="checkout-discount">
            {discount ? (
              <div className="checkout-note checkout-discount-applied">
                <p>
                  <strong>{discount.code}</strong>
                  <br />
                  {partialDiscount
                    ? checkoutCopy.codeAppliedSome(discount.percentOff)
                    : checkoutCopy.codeApplied(discount.percentOff)}
                </p>
                <button
                  type="button"
                  onClick={removeDiscount}
                  className="checkout-link"
                >
                  {checkoutCopy.codeRemove}
                </button>
              </div>
            ) : (
              <>
                <label htmlFor="checkout-discount-code">
                  {checkoutCopy.codeLabel}
                </label>
                <div className="checkout-discount-row">
                  <input
                    id="checkout-discount-code"
                    className="checkout-input"
                    value={discountInput}
                    onChange={(event) => {
                      setDiscountInput(event.target.value);
                      if (discountError) setDiscountError("");
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        void applyDiscount();
                      }
                    }}
                    placeholder={checkoutCopy.codePlaceholder}
                    aria-invalid={Boolean(discountError)}
                    aria-describedby={
                      discountError ? "checkout-discount-error" : undefined
                    }
                    disabled={applyingDiscount || loading}
                  />
                  <button
                    type="button"
                    onClick={() => void applyDiscount()}
                    disabled={
                      applyingDiscount || loading || !discountInput.trim()
                    }
                    className="checkout-button checkout-button-secondary"
                  >
                    {applyingDiscount
                      ? checkoutCopy.codeApplying
                      : checkoutCopy.codeApply}
                  </button>
                </div>
              </>
            )}
            {discountError && (
              <p
                id="checkout-discount-error"
                className="checkout-error"
                role="alert"
              >
                {discountError}
              </p>
            )}
          </div>
          {discount && (
            <dl className="checkout-breakdown">
              <div>
                <dt>{checkoutCopy.subtotal}</dt>
                <dd>{formatPrice(quote.total, quote.currency)}</dd>
              </div>
              <div>
                <dt>
                  {checkoutCopy.discount} (
                  {partialDiscount ? discount.code : `−${discount.percentOff}%`}
                  )
                </dt>
                <dd>−{formatPrice(discountAmount, quote.currency)}</dd>
              </div>
            </dl>
          )}
          <div className="checkout-total" aria-live="polite">
            <span>{checkoutCopy.total}</span>
            <span className="checkout-total-amount">
              {discount && (
                <s className="checkout-price-was">
                  <span className="sr-only">{checkoutCopy.priceWas} </span>
                  {formatPrice(quote.total, quote.currency)}
                </s>
              )}
              <strong>{formatPrice(payableTotal, quote.currency)}</strong>
            </span>
          </div>
          <p className="checkout-price-note">{checkoutCopy.quoteAuthority}</p>
        </>
      ) : (
        <div className="checkout-status">
          <strong>
            {quote?.excluded?.length
              ? checkoutCopy.ownedTitle
              : checkoutCopy.emptyTitle}
          </strong>
          <p>
            {quote?.excluded?.length
              ? checkoutCopy.ownedBody
              : checkoutCopy.emptyBody}
          </p>
          {Boolean(quote?.excluded?.length) && (
            <p>{quote?.excluded?.map((item) => item.title).join(", ")}</p>
          )}
          <a
            className="checkout-button checkout-button-secondary"
            href={checkoutLandingUrl}
          >
            {checkoutCopy.catalog}
          </a>
        </div>
      )}
    </>
  );
  const mayContinue =
    !loading &&
    !changingAccount &&
    !loadingQuote &&
    !applyingDiscount &&
    Boolean(quote?.items.length);
  // Offers the launch coupon, or confirms it once applied. Another code the
  // buyer already applied is left alone.
  const launchCode = launchCouponCode;
  const launchApplied = discount?.code.toUpperCase() === launchCode;
  const launchBar =
    launchCode && quote?.items.length && (!discount || launchApplied) ? (
      <CheckoutLaunchBar
        code={launchCode}
        label={checkoutCopy.launchLabel}
        text={
          launchApplied ? checkoutCopy.launchApplied : checkoutCopy.launchOffer
        }
        action={
          !launchApplied && (
            <button
              type="button"
              className="checkout-link"
              disabled={applyingDiscount || loading}
              onClick={() => {
                setDiscountInput(launchCode);
                void applyDiscount(launchCode);
              }}
            >
              {applyingDiscount
                ? checkoutCopy.codeApplying
                : checkoutCopy.codeApply}
            </button>
          )
        }
      />
    ) : null;

  return (
    <CheckoutLayout
      banner={launchBar}
      summary={summary}
      userEmail={user?.email}
      backHref={checkoutLandingUrl}
      loginHref={loginPath}
    >
      {user ? (
        <>
          <div className="checkout-signed-in">
            <CircleCheck aria-hidden="true" size={30} />
            <strong>{checkoutCopy.signedIn}</strong>
            <p>{checkoutCopy.signedInBody}</p>
            <button
              type="button"
              className="checkout-link"
              disabled={loading || changingAccount}
              onClick={changeCheckoutAccount}
            >
              {changingAccount
                ? checkoutCopy.changingAccount
                : checkoutCopy.changeAccount}
            </button>
          </div>
          {error && (
            <p className="checkout-error" role="alert">
              {error}
            </p>
          )}
          <button
            type="button"
            onClick={pay}
            disabled={!mayContinue}
            className="checkout-button checkout-button-primary"
          >
            {freeOrder
              ? loading
                ? checkoutCopy.payingFree
                : checkoutCopy.payFree
              : loading
                ? checkoutCopy.paying
                : checkoutCopy.pay}
            <span aria-hidden="true">↗</span>
          </button>
          <p className="checkout-security">
            <LockKeyhole size={14} aria-hidden="true" />
            {freeOrder ? checkoutCopy.secureFree : checkoutCopy.secure}
          </p>
        </>
      ) : (
        <form onSubmit={payAsGuest} className="checkout-form" noValidate>
          <fieldset disabled={loading} className="checkout-fields">
            <div className="checkout-field">
              <label htmlFor="checkout-email">{checkoutCopy.email}</label>
              <input
                id="checkout-email"
                name="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                required
                value={guestEmail}
                onChange={(event) => {
                  setGuestEmail(event.target.value);
                  clearFieldError("email");
                  setOwnedBySignIn(false);
                }}
                placeholder={checkoutCopy.emailPlaceholder}
                aria-invalid={Boolean(fieldErrors.email)}
                aria-describedby="checkout-email-help"
                className="checkout-input"
              />
              <p id="checkout-email-help" className="checkout-field-help">
                {checkoutCopy.emailHelp}
              </p>
              {fieldErrors.email && (
                <p className="checkout-error" role="alert">
                  {fieldErrors.email}
                </p>
              )}
            </div>
          </fieldset>
          {error && (
            <div
              id="checkout-feedback"
              tabIndex={-1}
              className="checkout-error"
              role="alert"
            >
              <p>{error}</p>
              {ownedBySignIn && <a href={loginPath}>{checkoutCopy.login}</a>}
            </div>
          )}
          <button
            type="submit"
            disabled={!mayContinue}
            className="checkout-button checkout-button-primary"
          >
            {freeOrder
              ? loading
                ? checkoutCopy.payingFree
                : checkoutCopy.payFree
              : loading
                ? checkoutCopy.paying
                : checkoutCopy.pay}
            <span aria-hidden="true">↗</span>
          </button>
          <p className="checkout-security">
            <LockKeyhole size={14} aria-hidden="true" />
            {freeOrder ? checkoutCopy.secureFree : checkoutCopy.secure}
          </p>
          <p className="checkout-login-prompt">
            {checkoutCopy.existingAccount}{" "}
            <button
              type="button"
              className="checkout-link"
              onClick={() => go(loginPath)}
            >
              {checkoutCopy.login}
            </button>
          </p>
        </form>
      )}
      <p className="checkout-account-note">{checkoutCopy.accountNote}</p>
    </CheckoutLayout>
  );
}

function CheckoutSuccess({ user }: { user?: { email: string } }) {
  const orderId =
    new URLSearchParams(window.location.search).get("order") ?? "";
  const [status, setStatus] = useState<
    "pending" | "paid" | "error" | "timeout"
  >(orderId ? "pending" : "error");
  const [courses, setCourses] = useState<
    Array<{ slug: string; title: string }>
  >([]);
  // Set for a guest checkout: what the buyer does next with their account.
  const [account, setAccount] = useState<GuestAccount | null>(null);
  useEffect(() => {
    if (!orderId) return;
    let active = true;
    let tries = 0;
    let timer: ReturnType<typeof setTimeout>;
    const poll = () => {
      apiFetch<{
        status: string;
        currency: string;
        amountMinor: number;
        courses: Array<{ slug: string; title: string }>;
        account: GuestAccount | null;
      }>(`/api/checkout/orders/${orderId}`)
        .then((data) => {
          if (!active) return;
          setCourses(data.courses);
          setAccount(data.account);
          if (data.status === "paid") {
            setStatus("paid");
            trackPurchase(
              orderId,
              { amountMinor: data.amountMinor, currency: data.currency },
              data.courses.map((course) => course.slug),
            );
            track("checkout_success_viewed", {
              order_id: orderId,
              needs_activation: data.account?.state === "activate",
            });
          } else if (++tries > 20) setStatus("timeout");
          else timer = setTimeout(poll, 2000);
        })
        .catch(() => {
          if (active) setStatus("error");
        });
    };
    poll();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [orderId]);
  return (
    <div className="bg-paper min-h-dvh">
      <PlatformHeader user={user} />
      <main className="platform-grid mx-auto max-w-3xl px-5 py-16 sm:px-8">
        {status === "paid" && account ? (
          <GuestActivation
            orderId={orderId}
            account={account}
            courseSlug={courses[0]?.slug ?? null}
          />
        ) : status === "paid" ? (
          <div className="border-ink bg-grape shadow-brutal-lg border-surface p-8 text-center">
            <CircleCheck className="mx-auto" size={48} />
            <h1 className="font-display mt-4 text-4xl font-extrabold">
              ¡Listo! Ya tienes acceso
            </h1>
            <p className="mt-3">
              Tu pago se confirmó y desbloqueamos{" "}
              {courses.length === 1 ? "tu curso" : "tus cursos"}.
            </p>
            <button
              onClick={() => go("/courses")}
              className={`${btnPrimary} mt-7 cursor-pointer px-6 py-4 text-lg`}
            >
              Ir a mis cursos →
            </button>
          </div>
        ) : status === "pending" ? (
          <div className="border-ink bg-bone shadow-brutal-lg border-surface p-8 text-center">
            <h1 className="font-display text-3xl font-extrabold">
              Confirmando tu pago…
            </h1>
            <p className="text-ink-soft mt-3">
              Esto tarda unos segundos. No cierres esta página.
            </p>
          </div>
        ) : (
          <div className="border-ink bg-bone shadow-brutal-lg border-surface p-8 text-center">
            <h1 className="font-display text-3xl font-extrabold">
              {status === "timeout"
                ? "Tu pago sigue procesándose"
                : "No encontramos tu orden"}
            </h1>
            <p className="text-ink-soft mt-3">
              {status === "timeout"
                ? "Si ya pagaste, tus cursos aparecerán en tu panel en breve."
                : "Vuelve al catálogo o revisa tu panel."}
            </p>
            <button
              onClick={() => go("/courses")}
              className={`${btnPrimary} mt-7 cursor-pointer px-6 py-4 text-lg`}
            >
              Ir a mi panel
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

function CheckoutCancel({ user }: { user?: { email: string } }) {
  return (
    <div className="bg-paper min-h-dvh">
      <PlatformHeader user={user} />
      <main className="platform-grid mx-auto max-w-3xl px-5 py-16 sm:px-8">
        <div className="border-ink bg-bone shadow-brutal-lg border-surface p-8 text-center">
          <h1 className="font-display text-3xl font-extrabold">
            Pago cancelado
          </h1>
          <p className="text-ink-soft mt-3">
            No se hizo ningún cargo. Puedes retomar cuando quieras.
          </p>
          <a
            href="https://hey404.com/cursos"
            className="border-ink bg-ink font-display text-paper shadow-brutal border-surface mt-7 inline-block px-6 py-4 text-lg font-extrabold"
          >
            Volver al catálogo
          </a>
        </div>
      </main>
    </div>
  );
}

// What the dashboard's first screen depends on. App prefetches these behind its
// shell skeleton, so the overview paints complete in one go instead of piece by
// piece (0% jumping to the real progress, nav items popping in, and so on).
const catalogQueryOptions = queryOptions({
  queryKey: ["student", "courses"],
  queryFn: () => apiFetch<{ courses: ManagedCourse[] }>("/api/courses"),
});
// The lesson the student watched last (and the second they reached), for the
// overview's "Sigue viendo" card.
const continueQueryOptions = queryOptions({
  queryKey: ["student", "continue"],
  queryFn: () =>
    apiFetch<{
      continueLearning: {
        lessonId: string;
        positionSeconds: number;
        completed: boolean;
      } | null;
    }>("/api/education/dashboard"),
});
const navigationQueryOptions = queryOptions({
  queryKey: ["student", "navigation"],
  queryFn: () => apiFetch<NavigationContext>("/api/education/navigation"),
});
const fetchLearning = () =>
  Promise.all([
    apiFetch<{
      completedLessonIds: string[];
      weeklyCompletedMinutes: number;
      weeklyGoalMinutes: number;
      maxWeeklyMinutes: number;
      streakDays: number;
    }>("/api/learning/progress"),
    apiFetch<{ certificates: Certificate[] }>("/api/learning/certificates"),
  ]);
// The dashboard's mount reads this through the cache (prefetched by App, fresh
// for 30 s); later refreshes after marking a lesson call fetchLearning directly.
const learningQueryOptions = queryOptions({
  queryKey: ["student", "learning", "initial"],
  queryFn: fetchLearning,
  staleTime: 30_000,
});

function Dashboard({
  user,
  administrator = false,
}: {
  user: { name: string; email: string };
  administrator?: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  // Shared bounce for the in-progress course cards (fired from the stat card).
  // Observe the admin permissions AdminPanel already fetched (same query key),
  // so the mobile drawer can show the admin sections the user can actually see.
  const adminBoot = useQuery({
    queryKey: ["admin", "bootstrap"],
    queryFn: () => null,
    enabled: false,
  });
  const adminPerms =
    (
      adminBoot.data as unknown as
        { current?: { permissions?: AdminSection[] } } | undefined
    )?.current?.permissions ?? [];
  const [activeLesson, setActiveLesson] = useState("");
  const [mediaLoading, setMediaLoading] = useState(true);
  const [mediaError, setMediaError] = useState(false);
  const [nextCountdown, setNextCountdown] = useState<number | null>(null);
  const [playbackPosition, setPlaybackPosition] = useState(0);
  // Which lesson playbackPosition belongs to: right after switching lessons
  // it still holds the previous lesson's second until the new player reports.
  const [positionLesson, setPositionLesson] = useState("");
  const [resumePosition, setResumePosition] = useState(0);
  // Set by the "?lesson=&t=" deep link, consumed when that lesson becomes
  // active (the lesson-change effect would otherwise reset to 0).
  // Deep link to a class's comments (moderation, reply notifications):
  // /courses/:slug?lesson=<id>#comentarios scrolls there once that lesson's
  // comments are on screen.
  const commentsTarget = useRef(
    window.location.hash === "#comentarios"
      ? new URLSearchParams(window.location.search).get("lesson")
      : null,
  );
  const pendingResume = useRef<{ lessonId: string; seconds: number } | null>(
    null,
  );
  const videoRef = useRef<HTMLVideoElement>(null);
  const lastPositionSave = useRef(0);
  const [comment, setComment] = useState("");
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const commentBox = useRef<HTMLTextAreaElement>(null);
  // The class comments open as a short preview (COMMENT_PREVIEW posts in
  // total, replies included) with "Ver todos" for the rest.
  const [allComments, setAllComments] = useState(false);
  const [openThreads, setOpenThreads] = useState<string[]>([]);
  // "Responder" moves you to the composer, already pointed at that thread.
  const replyTo = (id: string) => {
    setReplyingTo(id);
    setOpenThreads((ids) => (ids.includes(id) ? ids : [...ids, id]));
    commentBox.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    commentBox.current?.focus({ preventScroll: true });
  };
  // Weekly goal in minutes (UNB2-165), matching account settings and the widget.
  const [weeklyGoal, setWeeklyGoal] = useState(120);
  const [weeklyGoalDraft, setWeeklyGoalDraft] = useState(120);
  const [maxWeeklyMinutes, setMaxWeeklyMinutes] = useState(120);
  const [editingWeeklyGoal, setEditingWeeklyGoal] = useState(false);
  const [completedLessons, setCompletedLessons] = useState<string[]>([]);
  const [weeklyCompleted, setWeeklyCompleted] = useState(0);
  // Consecutive-days streak read from learning_activity (UNB2-141).
  const [streakDays, setStreakDays] = useState(0);
  const weeklyProgress = weeklyCompleted;
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewBody, setReviewBody] = useState("");
  const [reviewSaved, setReviewSaved] = useState(false);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [progressLoading, setProgressLoading] = useState(true);
  const [comments, setComments] = useState<
    Array<{
      id: string;
      userName: string;
      userAvatarUrl?: string | null;
      body: string;
      parentId: string | null;
      createdAt: string;
      question?: boolean;
      answered?: boolean;
      accepted?: boolean;
    }>
  >([]);
  const applyLearning = ([progress, issued]: Awaited<
    ReturnType<typeof fetchLearning>
  >) => {
    setCompletedLessons(progress.completedLessonIds);
    setWeeklyCompleted(progress.weeklyCompletedMinutes);
    setWeeklyGoal(progress.weeklyGoalMinutes);
    setMaxWeeklyMinutes(progress.maxWeeklyMinutes);
    setStreakDays(progress.streakDays);
    setCertificates(issued.certificates);
  };
  const loadLearning = () => fetchLearning().then(applyLearning);
  useEffect(() => {
    queryClient
      .fetchQuery(learningQueryOptions)
      .then(applyLearning)
      .catch(() => undefined)
      .finally(() => setProgressLoading(false));
  }, []);
  const loadComments = () =>
    apiFetch<{ comments: typeof comments }>(
      `/api/community/lessons/${activeLesson}/comments`,
    ).then((result) => setComments(result.comments));
  useEffect(() => {
    apiFetch<{ comments: typeof comments }>(
      `/api/community/lessons/${activeLesson}/comments`,
    )
      .then((result) => {
        setComments(result.comments);
        setAllComments(false);
        setOpenThreads([]);
        if (commentsTarget.current && commentsTarget.current === activeLesson) {
          commentsTarget.current = null;
          requestAnimationFrame(() =>
            document
              .getElementById("comentarios")
              ?.scrollIntoView({ behavior: "smooth", block: "start" }),
          );
        }
      })
      .catch(() => setComments([]));
  }, [activeLesson]);
  const current =
    window.location.pathname === "/" ? "/overview" : window.location.pathname;
  const playerOpen = current.startsWith("/courses/");
  const [courses, setCourses] = useState<
    Array<{
      slug: string;
      title: string;
      progress: number;
      next: string;
      accent: string;
      lessons: number;
    }>
  >([]);
  const [managedCourses, setManagedCourses] = useState<ManagedCourse[]>([]);
  const catalogQuery = useQuery(catalogQueryOptions);
  const continueQuery = useQuery(continueQueryOptions);
  const navigationQuery = useQuery(navigationQueryOptions);
  const catalogLoading = catalogQuery.isPending;
  const catalogError = catalogQuery.isError;
  useEffect(() => {
    if (catalogQuery.data) {
      const available = catalogQuery.data.courses.filter(
        (course) => course.enrolled,
      );
      setManagedCourses(available);
      setCourses(
        available.map((course, index) => ({
          slug: course.slug,
          title: course.title,
          progress: 0,
          next: "Empezar curso",
          accent: ["bg-acid", "bg-bubble", "bg-lemon", "bg-grape"][index % 4]!,
          lessons: course.modules.reduce(
            (total, module) => total + module.lessons.length,
            0,
          ),
        })),
      );
    }
  }, [catalogQuery.data]);
  useEffect(() => {
    if (!managedCourses.length) return;
    setCourses((current) =>
      current.map((course) => {
        const managed = managedCourses.find(
          (item) => item.slug === course.slug,
        );
        if (!managed) return course;
        const lessonIds = managed.modules.flatMap((module) =>
          module.lessons.map((lesson) => lesson.id),
        );
        const completed = lessonIds.filter((lessonId) =>
          completedLessons.includes(lessonId),
        ).length;
        const progress = lessonIds.length
          ? Math.round((completed / lessonIds.length) * 100)
          : 0;
        const nextLesson = managed.modules
          .flatMap((module) => module.lessons)
          .find((lesson) => !completedLessons.includes(lesson.id));
        return {
          ...course,
          progress,
          next:
            progress === 100
              ? "Curso completado"
              : (nextLesson?.title ?? "Empezar curso"),
        };
      }),
    );
  }, [completedLessons, managedCourses]);
  const courseSlug = current.split("/")[2] ?? "";
  const managedCourse = managedCourses.find(
    (course) => course.slug === courseSlug,
  );
  // The full catalog (published, with an enrolled flag) — lets the player gate
  // tell "you don't have access" (exists, not enrolled) from "not found".
  const catalogCourse = catalogQuery.data?.courses.find(
    (course) => course.slug === courseSlug,
  );
  const modules: PlayerModule[] = managedCourse
    ? managedCourse.modules.map((module, index) => ({
        number: String(index + 1).padStart(2, "0"),
        title: module.title,
        progress: `${module.lessons.filter((lesson) => completedLessons.includes(lesson.id)).length}/${module.lessons.length}`,
        lessons: module.lessons.map((lesson) => ({
          id: lesson.id,
          title: lesson.title,
          time: `${String(lesson.durationMinutes).padStart(2, "0")}:00`,
          durationMinutes: lesson.durationMinutes,
          content: lesson.content,
          videoUrl: lesson.videoUrl,
          videoSource: lesson.videoSource,
          thumbnailUrl: lesson.thumbnailUrl,
          kind: lesson.kind,
          captionsUrl: lesson.captionsUrl,
          locked: lesson.locked,
        })),
      }))
    : [];
  const lessonEntries = modules.flatMap((module) =>
    module.lessons.map((lesson) => ({ ...lesson, module })),
  );
  const selectedLesson =
    lessonEntries.find((lesson) => lesson.id === activeLesson) ??
    lessonEntries[0];
  // The description's "Videos recomendados" list becomes cards under it.
  const lessonDescription = splitRecommendedLessons(
    selectedLesson?.content ?? "",
  );
  const recommendedLessons = lessonDescription.links.flatMap((link) => {
    const lesson = lessonEntries.find((entry) => entry.id === link.id);
    return lesson && lesson.id !== selectedLesson?.id
      ? [{ ...lesson, reason: link.reason, at: link.at }]
      : [];
  });
  // Right-column accordion: which modules are expanded. Everything starts
  // collapsed except the module of the lesson being watched; once the student
  // toggles one, their choice holds until they switch course, and moving to a
  // lesson in another module opens that module too.
  const currentModule = selectedLesson?.module.number;
  const [openModules, setOpenModules] = useState<string[] | null>(null);
  useEffect(() => setOpenModules(null), [courseSlug]);
  useEffect(() => {
    if (!currentModule) return;
    setOpenModules((open) =>
      open && !open.includes(currentModule) ? [...open, currentModule] : open,
    );
  }, [currentModule]);
  const expandedModules = openModules ?? (currentModule ? [currentModule] : []);
  const toggleModule = (number: string) =>
    setOpenModules(
      expandedModules.includes(number)
        ? expandedModules.filter((item) => item !== number)
        : [...expandedModules, number],
    );
  useEffect(() => {
    const course = managedCourses.find((item) => item.slug === courseSlug);
    if (!course) return;
    // Deep link from "Guardadas": /courses/:slug?lesson=<id> opens that exact
    // lesson. Honour it once, then strip the param so a later learning reload
    // (marking a lesson complete refetches) can't yank the player back here
    // after the student has moved on — the same hazard the guard below covers.
    const requested = new URLSearchParams(window.location.search).get("lesson");
    const requestedInCourse =
      requested &&
      course.modules.some((module) =>
        module.lessons.some((lesson) => lesson.id === requested),
      );
    if (requestedInCourse) {
      const startAt = Number(
        new URLSearchParams(window.location.search).get("t") ?? 0,
      );
      pendingResume.current = {
        lessonId: requested,
        seconds: Number.isFinite(startAt) ? Math.max(0, startAt) : 0,
      };
      // Already on that lesson: the lesson-change effect won't fire.
      if (requested === activeLesson)
        setResumePosition(pendingResume.current.seconds);
      setActiveLesson(requested);
      const url = new URL(window.location.href);
      url.searchParams.delete("lesson");
      url.searchParams.delete("t");
      window.history.replaceState({}, "", url.pathname + url.search);
      return;
    }
    // Jump to the first lesson only when the current selection isn't part of
    // this course (first open / course switch) — NOT on every learning reload.
    // Marking a lesson complete reloads learning; without this guard that would
    // yank the player back to lesson 1 and kill the auto-advance countdown.
    const inCourse = course.modules.some((module) =>
      module.lessons.some((lesson) => lesson.id === activeLesson),
    );
    if (!inCourse) {
      const firstLesson = course.modules[0]?.lessons[0];
      if (firstLesson) setActiveLesson(firstLesson.id);
    }
  }, [courseSlug, managedCourses, activeLesson]);
  useEffect(() => {
    setMediaLoading(true);
    setMediaError(false);
    setNextCountdown(null);
    // A new lesson starts from 0 unless the overview asked to resume it.
    const pending = pendingResume.current;
    setResumePosition(pending?.lessonId === activeLesson ? pending.seconds : 0);
    pendingResume.current = null;
    if (activeLesson)
      track("lesson_opened", { lesson_id: activeLesson, course: courseSlug });
    // Only on a lesson change; the course is context, not a trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLesson]);
  const courseLessonCount = lessonEntries.length;
  const completedCount = lessonEntries.filter((lesson) =>
    completedLessons.includes(lesson.id),
  ).length;
  const courseProgress = courseLessonCount
    ? Math.round((completedCount / courseLessonCount) * 100)
    : 0;
  const setLessonCompleted = async (lessonId: string, completed: boolean) => {
    const previous = completedLessons;
    setCompletedLessons((current) =>
      completed
        ? Array.from(new Set([...current, lessonId]))
        : current.filter((id) => id !== lessonId),
    );
    try {
      await apiFetch(`/api/learning/lessons/${lessonId}/progress`, {
        method: "PUT",
        body: JSON.stringify({ completed }),
      });
      await loadLearning();
    } catch {
      setCompletedLessons(previous);
    }
  };
  const markLessonCompleteLocally = (lessonId: string) => {
    if (!completedLessons.includes(lessonId))
      track("lesson_completed", { lesson_id: lessonId, course: courseSlug });
    setCompletedLessons((current) =>
      current.includes(lessonId) ? current : [...current, lessonId],
    );
    void loadLearning();
  };
  // Report playback position (throttled to ~15s). The server auto-completes a
  // video lesson once ~90% is watched (UNB2-61) and reports it back, so the
  // player reflects completion live. `force` flushes now (e.g. on ended).
  const reportPosition = (
    position: number,
    opts?: { force?: boolean; watched?: number },
  ) => {
    setPlaybackPosition(position);
    setPositionLesson(activeLesson);
    if (!opts?.force && position - lastPositionSave.current < 15) return;
    lastPositionSave.current = position;
    apiFetch<{ completed?: boolean }>(
      `/api/education/lessons/${activeLesson}/position`,
      {
        method: "PUT",
        body: JSON.stringify({
          positionSeconds: position,
          watchedSeconds: opts?.watched ?? position,
        }),
      },
    )
      .then((res) => {
        if (res?.completed && !completedLessons.includes(activeLesson))
          markLessonCompleteLocally(activeLesson);
      })
      .catch(() => undefined);
  };
  // Auto-advance: when a lesson video ends, mark it complete and count down to the
  // next lesson (cancelable). Wired to Mux + HTML5 <video>; Vimeo is legacy (Mux is
  // the upload target now) and would need its player SDK, so it doesn't auto-advance.
  const nextLesson =
    lessonEntries[
      lessonEntries.findIndex((lesson) => lesson.id === selectedLesson?.id) + 1
    ];
  const handleLessonEnded = () => {
    const durationSeconds = (selectedLesson?.durationMinutes ?? 0) * 60;
    if (durationSeconds > 0) {
      // Full watch → the position endpoint auto-completes server-side.
      reportPosition(durationSeconds, {
        force: true,
        watched: durationSeconds,
      });
    } else if (
      selectedLesson &&
      !completedLessons.includes(selectedLesson.id)
    ) {
      // No measurable duration: fall back to marking complete directly.
      void setLessonCompleted(selectedLesson.id, true);
    }
    if (nextLesson) setNextCountdown(5);
  };
  useEffect(() => {
    if (nextCountdown === null) return;
    if (nextCountdown <= 0) {
      setNextCountdown(null);
      if (nextLesson) setActiveLesson(nextLesson.id);
      return;
    }
    const timer = setTimeout(
      () => setNextCountdown((value) => (value ?? 0) - 1),
      1000,
    );
    return () => clearTimeout(timer);
  }, [nextCountdown, nextLesson]);
  useEffect(() => {
    if (playerOpen && selectedLesson) {
      document.title = `${selectedLesson.title} — hey404`;
    }
  }, [playerOpen, selectedLesson]);
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarCollapsed();
  // The weekly widget fades in when the sidebar expands, not on first paint,
  // so the dashboard appears in one piece.
  const [weeklyWidgetMotion, setWeeklyWidgetMotion] = useState(false);
  // Read from the query (prefetched by App) rather than the courses state,
  // which only fills in a render later.
  const hasEnrolledCourse = Boolean(
    catalogQuery.data?.courses.some((course) => course.enrolled),
  );
  const visibility = navigationQuery.data?.visibility;
  const counts = navigationQuery.data?.counts;
  // Courses on sale only: staff can open coming-soon ones, but they are not
  // "the course" a student continues.
  const onSaleSlugs = new Set(
    (catalogQuery.data?.courses ?? [])
      .filter((course) => course.saleStatus !== "coming_soon")
      .map((course) => course.slug),
  );
  const continuable = courses.filter((course) => onSaleSlugs.has(course.slug));
  // With no course of their own, the overview points to the one on sale.
  const onSaleCourse = (catalogQuery.data?.courses ?? []).find(
    (course) => course.saleStatus !== "coming_soon",
  );
  const currentCourseSlug = (
    continuable.find((course) => course.progress < 100) ?? continuable[0]
  )?.slug;
  // Overview "Sigue viendo": the last lesson watched in the current course;
  // if it's done (or nothing was watched yet), the first one still pending.
  const currentManaged = managedCourses.find(
    (course) => course.slug === currentCourseSlug,
  );
  const currentLessons = (currentManaged?.modules ?? []).flatMap(
    (module, index) =>
      module.lessons.map((lesson) => ({
        id: lesson.id,
        title: lesson.title,
        thumbnailUrl: lesson.thumbnailUrl,
        durationMinutes: lesson.durationMinutes,
        moduleNumber: String(index + 1).padStart(2, "0"),
        moduleTitle: module.title,
        content: lesson.content,
      })),
  );
  const lastViewed = continueQuery.data?.continueLearning;
  const lastIndex = currentLessons.findIndex(
    (lesson) => lesson.id === lastViewed?.lessonId,
  );
  const resumeIndex =
    lastIndex >= 0 && !lastViewed?.completed
      ? lastIndex
      : currentLessons.findIndex(
          (lesson, index) =>
            index > lastIndex && !completedLessons.includes(lesson.id),
        );
  const resumeLesson = currentLessons[resumeIndex];
  const resumeSeconds =
    resumeLesson && resumeIndex === lastIndex
      ? (lastViewed?.positionSeconds ?? 0)
      : 0;
  // The "Mis cursos" library is gone while hey404 has a single course (see
  // tag archive/mis-cursos-page): /courses forwards to that course, or to
  // the overview when there is none yet.
  useEffect(() => {
    if (current !== "/courses" || catalogQuery.isPending) return;
    window.history.replaceState(
      {},
      "",
      currentCourseSlug ? `/courses/${currentCourseSlug}` : "/overview",
    );
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, [current, currentCourseSlug, catalogQuery.isPending]);
  const navigation = [
    { href: "/overview", label: "Overview", icon: LayoutDashboard },
    // Straight into the class, skipping the "Mis cursos" library: the course
    // still in progress (else the most recent enrollment) opens in the player.
    {
      href: currentCourseSlug ? `/courses/${currentCourseSlug}` : "/overview",
      // hey404 has one course for now, so the entry names it as yours.
      label: "Mi curso",
      icon: BookOpen,
    },
    { href: "/guardadas", label: "Guardadas", icon: Bookmark },
    // Learning paths are out of the MVP for now (UNB2-170). Hidden; re-enable by
    // restoring show: visibility?.paths.
    { href: "/paths", label: "Rutas", icon: Map, show: false },
    {
      href: "/events",
      label: "Eventos",
      icon: CalendarDays,
      show: visibility?.events,
    },
    { href: "/teams", label: "Equipos", icon: Users, show: visibility?.teams },
    // Learning coach (AI) is out of the MVP until courses are transcribed and
    // the feature is built (UNB2-171). Hidden for now; re-enable by restoring
    // show: visibility?.coach.
    { href: "/coach", label: "Coach", icon: Sparkles, show: false },
    {
      href: "/certificates",
      label: "Certificados",
      icon: Award,
      show: visibility?.certificates,
    },
    { href: "/settings", label: "Configuración", icon: Settings },
    ...(administrator
      ? [{ href: "/admin/overview", label: "Administración", icon: Wrench }]
      : []),
  ].filter((item) => !("show" in item) || item.show === true);
  // `desktop` is the fixed column, which can collapse to icons; the mobile
  // drawer always shows the full menu.
  const Sidebar = (desktop = false) => {
    const collapsed = desktop && sidebarCollapsed;
    return (
      <aside
        className={`border-ink bg-ink text-paper border-r-(length:--border-surface) flex h-full flex-col overflow-hidden py-5 transition-[padding] duration-300 ${collapsed ? "px-2" : "px-3"}`}
      >
        <p
          className={`text-paper/70 mb-3 px-3 font-mono text-xs font-bold uppercase tracking-[0.2em] ${collapsed ? "sr-only" : ""}`}
        >
          Mi espacio
        </p>
        <nav className="space-y-1">
          {navigation.map((item) => {
            const Icon = item.icon;
            const active =
              current === item.href ||
              // Matched by route, not by the label, so renaming it is safe.
              (item.href.startsWith("/courses/") &&
                current.startsWith("/courses")) ||
              (item.href === "/admin/overview" && current.startsWith("/admin"));
            return (
              <button
                key={item.href}
                onClick={() => {
                  go(item.href);
                  setMenuOpen(false);
                }}
                // V30 nav link: regular weight; the accent fill and ink ring mark hover
                // and the current page without changing the label's size.
                className={`sidebar-link rounded-control relative flex min-h-11 w-full cursor-pointer items-center py-2.5 text-left text-sm transition-[background-color,color,gap,padding] duration-300 ${collapsed ? "justify-center gap-0 px-0" : "gap-3 px-3"} ${active ? "bg-acid text-ink shadow-nav" : "text-paper/90 hover:bg-bubble hover:text-ink hover:shadow-nav"}`}
                aria-current={active ? "page" : undefined}
                title={collapsed ? item.label : undefined}
              >
                <Icon
                  size={18}
                  strokeWidth={active ? 2.6 : 2}
                  className="shrink-0"
                  aria-hidden="true"
                />
                <span className={sidebarLabel(collapsed)}>{item.label}</span>
                {active && !collapsed && (
                  <ChevronRight className="ml-auto" size={16} />
                )}
              </button>
            );
          })}
        </nav>
        {hasEnrolledCourse && !collapsed && (
          <div
            className={`${weeklyWidgetMotion ? "sidebar-fade-in" : ""} border-paper/15 bg-paper/8 mt-7 border p-3`}
          >
            <div className="flex items-center justify-between font-mono text-xs">
              <span className="text-paper/60">AVANCE SEMANAL</span>
              <strong className="text-acid">
                {Math.min(weeklyProgress, weeklyGoal)}/{weeklyGoal} min
              </strong>
            </div>
            <div className="bg-paper/15 mt-3 h-1.5 overflow-hidden">
              <div
                className="bg-acid h-full transition-[width] duration-500"
                style={{
                  width: `${Math.min((weeklyProgress / weeklyGoal) * 100, 100)}%`,
                }}
              />
            </div>
            {streakDays > 0 && (
              <p className="text-paper/75 mt-3 flex items-center gap-1.5 font-mono text-xs font-bold">
                <Flame className="text-acid" size={13} />
                {streakDays}{" "}
                {streakDays === 1 ? "día seguido" : "días seguidos"}
              </p>
            )}
            {editingWeeklyGoal ? (
              <div className="mt-4">
                <p className="text-paper/55 text-xs leading-relaxed">
                  ¿Cuántos minutos quieres completar por semana?
                </p>
                <input
                  type="number"
                  min={15}
                  max={maxWeeklyMinutes}
                  value={weeklyGoalDraft}
                  onChange={(event) =>
                    setWeeklyGoalDraft(Number(event.target.value))
                  }
                  className="border-paper/30 bg-paper/10 text-paper focus:border-acid mt-3 w-full border px-3 py-2 font-mono text-sm outline-none"
                  aria-label="Objetivo semanal en minutos"
                />
                <p className="text-paper/70 mt-1 font-mono text-[9px]">
                  Máx {maxWeeklyMinutes} min · el total de tus cursos
                </p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setWeeklyGoalDraft(weeklyGoal);
                      setEditingWeeklyGoal(false);
                    }}
                    className="border-paper/20 text-paper/60 cursor-pointer border py-2 font-mono text-[9px] font-bold"
                  >
                    CANCELAR
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // Clamp to [15, total of the student's courses]; the server
                      // enforces the same bound.
                      const value = Math.min(
                        Math.max(Math.round(weeklyGoalDraft) || 15, 15),
                        maxWeeklyMinutes,
                      );
                      setWeeklyGoal(value);
                      setWeeklyGoalDraft(value);
                      setEditingWeeklyGoal(false);
                      apiFetch("/api/learning/weekly-goal", {
                        method: "PUT",
                        body: JSON.stringify({ minutes: value }),
                      }).catch(() => setWeeklyGoal(weeklyGoal));
                    }}
                    className="bg-acid text-ink cursor-pointer py-2 font-mono text-[9px] font-bold"
                  >
                    GUARDAR
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-paper/55 mt-3 text-xs leading-relaxed">
                  {weeklyProgress >= weeklyGoal
                    ? "Objetivo cumplido. Buen trabajo esta semana."
                    : `${weeklyGoal - weeklyProgress} min más para cumplir tu objetivo.`}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setWeeklyGoalDraft(weeklyGoal);
                    setEditingWeeklyGoal(true);
                  }}
                  className="text-paper/70 hover:text-acid mt-2 inline-block cursor-pointer py-1.5 font-mono text-[9px] font-bold underline underline-offset-4"
                >
                  CONFIGURAR OBJETIVO
                </button>
              </>
            )}
          </div>
        )}
        <div
          className={`mt-auto flex pb-2 pt-6 ${collapsed ? "justify-center" : "justify-end px-3"}`}
        >
          <Doodle
            name="cursor-lavender"
            float
            tilt={-10}
            className={collapsed ? "w-7" : "w-10"}
          />
        </div>
        {desktop && (
          <CollapseToggle
            collapsed={collapsed}
            onToggle={() => {
              setWeeklyWidgetMotion(true);
              setSidebarCollapsed((value) => !value);
            }}
          />
        )}
        <div
          className={`border-paper/15 flex items-center border-t pt-4 transition-[gap,padding] duration-300 ${collapsed ? "justify-center gap-0 px-0" : "gap-3 px-2"}`}
          title={collapsed ? user.name : undefined}
        >
          <Avatar
            name={user.name}
            url={navigationQuery.data?.avatarUrl}
            className="border-paper/30 bg-bubble text-ink h-9 w-9 text-xs"
          />
          <div
            className={`min-w-0 ${sidebarLabel(collapsed)} ${collapsed ? "" : "flex-1"}`}
          >
            <strong className="font-display block truncate text-sm">
              {user.name}
            </strong>
            <span className="text-paper/70 block truncate text-xs">
              {user.email}
            </span>
          </div>
        </div>
      </aside>
    );
  };

  const CoursePlayer = () => (
    <div className="course-player min-h-full">
      <motion.div
        className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)] xl:grid-cols-[minmax(0,1fr)_minmax(0,30rem)]"
        initial={false}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="min-w-0">
          <section
            id="lesson-video-shell"
            className="video-shell border-ink bg-ink border-surface relative aspect-video overflow-hidden"
          >
            {selectedLesson?.videoUrl ? (
              selectedLesson.videoSource === "mux" ? (
                <MuxLessonPlayer
                  lessonId={selectedLesson.id}
                  fullscreenElement="lesson-video-shell"
                  title={selectedLesson.title}
                  startAt={resumePosition}
                  onReady={() => setMediaLoading(false)}
                  onEnded={handleLessonEnded}
                  onPosition={(position: number) => reportPosition(position)}
                />
              ) : selectedLesson.videoSource === "vimeo" ? (
                <VimeoLessonPlayer
                  videoUrl={selectedLesson.videoUrl}
                  title={selectedLesson.title}
                  startAt={resumePosition}
                  onReady={() => setMediaLoading(false)}
                  onEnded={handleLessonEnded}
                  onPosition={(position) => reportPosition(position)}
                />
              ) : (
                <video
                  ref={videoRef}
                  src={selectedLesson.videoUrl}
                  onLoadedMetadata={(event) => {
                    const video = event.currentTarget;
                    if (resumePosition > 0) video.currentTime = resumePosition;
                    setMediaLoading(false);
                    // Start with sound; if the browser blocks audible autoplay
                    // (no user activation yet), start muted instead.
                    video.play().catch(() => {
                      video.muted = true;
                      void video.play().catch(() => undefined);
                    });
                  }}
                  onTimeUpdate={(event) =>
                    reportPosition(Math.floor(event.currentTarget.currentTime))
                  }
                  onEnded={handleLessonEnded}
                  onError={() => {
                    setMediaLoading(false);
                    setMediaError(true);
                  }}
                  controls
                  preload="metadata"
                  className="bg-ink absolute inset-0 h-full w-full"
                >
                  {selectedLesson.captionsUrl && (
                    <track
                      kind="captions"
                      src={selectedLesson.captionsUrl}
                      srcLang="es"
                      label="Español"
                      default
                    />
                  )}
                </video>
              )
            ) : (
              <div className="absolute inset-0 grid place-items-center px-6 text-center">
                <div>
                  <span className="border-ink bg-lemon text-ink font-display border-surface mx-auto grid h-14 w-14 place-items-center text-2xl font-extrabold">
                    404
                  </span>
                  <p className="text-paper mt-4 font-mono text-xs font-bold uppercase tracking-widest">
                    {selectedLesson
                      ? "Esta lección todavía no tiene video"
                      : "No hay una clase para mostrar"}
                  </p>
                </div>
              </div>
            )}
            {selectedLesson?.videoUrl && mediaLoading && !mediaError && (
              <div
                role="status"
                className="bg-ink absolute inset-0 z-10 grid place-items-center"
              >
                <div className="text-center">
                  <div className="flex items-end justify-center gap-1.5">
                    <span className="border-ink bg-acid border-chip h-4 w-4 animate-bounce [animation-delay:-300ms]" />
                    <span className="border-ink bg-bubble border-chip h-4 w-4 animate-bounce [animation-delay:-150ms]" />
                    <span className="border-ink bg-lemon border-chip h-4 w-4 animate-bounce" />
                  </div>
                  <p className="text-paper mt-5 font-mono text-xs font-bold uppercase tracking-widest">
                    Cargando video…
                  </p>
                </div>
              </div>
            )}
            {selectedLesson?.videoUrl && mediaError && (
              <div
                role="alert"
                className="bg-ink absolute inset-0 z-10 grid place-items-center px-6"
              >
                <div className="text-center">
                  <span className="border-ink bg-bubble text-ink font-display border-surface mx-auto grid h-14 w-14 place-items-center text-3xl font-extrabold">
                    !
                  </span>
                  <p className="text-paper mt-4 font-mono text-xs font-bold uppercase tracking-widest">
                    No pudimos cargar el video
                  </p>
                  <p className="text-paper/60 mt-1 font-mono text-xs">
                    Reintenta o recarga la página.
                  </p>
                </div>
              </div>
            )}
            {selectedLesson && (
              <RecommendedVideoCards
                key={selectedLesson.id}
                lessons={recommendedLessons}
                position={
                  positionLesson === selectedLesson.id ? playbackPosition : -1
                }
                onOpen={(id) => {
                  setActiveLesson(id);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            )}
            {nextCountdown !== null && nextLesson && (
              <div className="bg-ink/85 absolute inset-0 z-20 grid place-items-center px-6 text-center">
                <div>
                  <p className="text-paper/60 font-mono text-xs font-bold uppercase tracking-widest">
                    Próxima clase
                  </p>
                  <h3 className="text-paper font-display mt-2 text-2xl font-extrabold sm:text-3xl">
                    {nextLesson.title}
                  </h3>
                  <p className="text-paper mt-3 font-mono text-sm">
                    Continúa en {nextCountdown}…
                  </p>
                  <div className="mt-6 flex items-center justify-center gap-3">
                    <button
                      onClick={() => setNextCountdown(null)}
                      className={`${btnSecondary} px-4 py-2 text-sm`}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() => {
                        setNextCountdown(null);
                        setActiveLesson(nextLesson.id);
                      }}
                      className={`${btnPrimary} px-4 py-2 text-sm`}
                    >
                      Ver ahora →
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="absolute left-5 top-5 flex items-center gap-2">
              <span className="bg-acid h-2.5 w-2.5" />
              <span className="text-paper/70 font-mono text-xs uppercase tracking-widest">
                Módulo {selectedLesson?.module.number} ·{" "}
                {selectedLesson?.module.title}
              </span>
            </div>
          </section>
          <div className="border-ink bg-bone border-x-(length:--border-surface) border-b-(length:--border-surface) p-5 sm:p-7">
            <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-start">
              <div>
                <p className="text-ink-soft font-mono text-xs font-bold uppercase tracking-widest">
                  Clase{" "}
                  {lessonEntries.findIndex(
                    (lesson) => lesson.id === activeLesson,
                  ) + 1}{" "}
                  de {courseLessonCount}
                </p>
                {/* "Guardar clase" sits right by the title so it's always at
                    hand; it drops below the title when there's no room. */}
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-3">
                  <h1 className="font-display text-3xl font-extrabold sm:text-4xl">
                    {selectedLesson?.title}
                  </h1>
                  {selectedLesson && (
                    <LessonBookmark
                      lessonId={selectedLesson.id}
                      positionSeconds={playbackPosition}
                    />
                  )}
                </div>
                <p className="text-ink-soft mt-2 flex items-center gap-2 text-sm">
                  <Clock3 size={15} /> {selectedLesson?.time} minutos ·
                  Actualizado en julio 2026
                </p>
              </div>
              {/* Video lessons complete themselves by being watched (≈90%, or
                  the end when the duration is unknown; UNB2-61), so they show
                  no badge here: the lesson list already marks them done. Only
                  other kinds keep the manual toggle. */}
              {selectedLesson?.kind !== "video" && (
                <button
                  onClick={() =>
                    setLessonCompleted(
                      activeLesson,
                      !completedLessons.includes(activeLesson),
                    )
                  }
                  disabled={progressLoading}
                  className={`${completedLessons.includes(activeLesson) ? btnSecondary : btnAccent} shrink-0 px-4 py-3 text-sm`}
                >
                  <Check size={17} />{" "}
                  {completedLessons.includes(activeLesson)
                    ? "Clase completada"
                    : "Marcar como vista"}
                </button>
              )}
            </div>
            <div className="border-ink/15 mt-7 border-t pt-6">
              <h2 className="font-display text-lg font-extrabold">
                Sobre esta clase
              </h2>
              <Markdown className="text-ink-soft mt-3 max-w-3xl leading-relaxed">
                {lessonDescription.body ||
                  managedCourse?.description ||
                  "El contenido de esta clase estará disponible pronto."}
              </Markdown>
              <RecommendedLessons
                lessons={recommendedLessons}
                onOpen={(id) => {
                  setActiveLesson(id);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
              {!managedCourse && (
                <div className="mt-5 flex flex-wrap gap-2">
                  {["Modelo de datos", "Relaciones", "Buenas prácticas"].map(
                    (tag) => (
                      <span
                        key={tag}
                        className="border-ink/25 bg-paper border px-2.5 py-1 font-mono text-xs font-bold"
                      >
                        {tag}
                      </span>
                    ),
                  )}
                </div>
              )}
            </div>
          </div>
          {/* Lesson tools right after the description, before the
              conversation: resources, notes, transcript and practice. */}
          {selectedLesson && (
            <div className="mt-7">
              <LessonCompanion
                lessonId={selectedLesson.id}
                positionSeconds={playbackPosition}
                onResume={setResumePosition}
              />
            </div>
          )}
          {(managedCourse?.forum?.enabled ?? true) && (
            <section
              id="comentarios"
              className="border-ink bg-paper border-surface mt-7 scroll-mt-24 p-5 sm:p-7"
            >
              <CommunityHeader
                title="Comentarios de la clase"
                count={comments.length}
                description="Pregunta lo que no te quedó claro o comparte un avance. El equipo responde aquí."
              />
              <div className="mt-5">
                <ChatComposer
                  label={
                    replyingTo
                      ? "Tu respuesta"
                      : "Escribe un comentario o pregunta"
                  }
                  value={comment}
                  onChange={setComment}
                  textareaRef={commentBox}
                  placeholder={
                    replyingTo
                      ? "Escribe tu respuesta…"
                      : "Escribe un comentario o una pregunta…"
                  }
                  avatar={
                    <Avatar
                      name={user.name}
                      url={navigationQuery.data?.avatarUrl}
                      className="border-ink bg-acid h-9 w-9 shrink-0 text-xs"
                    />
                  }
                  context={
                    replyingTo && (
                      <span className="inline-flex items-center gap-2 text-xs">
                        <CommunityTag tone="bg-lemon">
                          <Reply size={11} aria-hidden="true" /> Respondiendo a{" "}
                          {comments.find((item) => item.id === replyingTo)
                            ?.userName ?? "un comentario"}
                        </CommunityTag>
                        <button
                          type="button"
                          onClick={() => setReplyingTo(null)}
                          aria-label="Cancelar respuesta"
                          className="text-ink-soft hover:text-ink cursor-pointer"
                        >
                          <X size={14} aria-hidden="true" />
                        </button>
                      </span>
                    )
                  }
                  onSubmit={async (event) => {
                    event.preventDefault();
                    if (!comment.trim()) return;
                    await apiFetch(
                      `/api/community/lessons/${activeLesson}/comments`,
                      {
                        method: "POST",
                        body: JSON.stringify({
                          body: comment.trim(),
                          parentId: replyingTo,
                          question: !replyingTo,
                        }),
                      },
                    );
                    setComment("");
                    setReplyingTo(null);
                    await loadComments();
                  }}
                />
              </div>
              {comments.length === 0 ? (
                <p className="text-ink-soft flex items-center gap-2 pt-5 text-sm">
                  <MessageCircle size={16} aria-hidden="true" />
                  Todavía no hay comentarios en esta clase. Abre la
                  conversación.
                </p>
              ) : (
                (() => {
                  // YouTube-style: newest comments first, replies folded under
                  // "N respuestas", COMMENT_PREVIEW comments before "Ver todos".
                  const roots = comments
                    .filter((item) => !item.parentId)
                    .sort((x, y) => y.createdAt.localeCompare(x.createdAt));
                  const shown = allComments
                    ? roots
                    : roots.slice(0, COMMENT_PREVIEW);
                  return (
                    <>
                      <ul className="mt-6 space-y-5">
                        {shown.map((item) => {
                          const replies = comments.filter(
                            (reply) => reply.parentId === item.id,
                          );
                          const open = openThreads.includes(item.id);
                          return (
                            <li key={item.id}>
                              <CommentBubble
                                name={item.userName}
                                avatarUrl={item.userAvatarUrl}
                                createdAt={item.createdAt}
                                body={item.body}
                                tags={
                                  item.question &&
                                  (item.answered ? (
                                    <CommunityTag tone="bg-acid">
                                      Respondida
                                    </CommunityTag>
                                  ) : (
                                    <CommunityTag tone="bg-lemon">
                                      Pregunta
                                    </CommunityTag>
                                  ))
                                }
                                actions={
                                  <MetaAction onClick={() => replyTo(item.id)}>
                                    Responder
                                  </MetaAction>
                                }
                                replyCount={replies.length}
                                repliesOpen={open}
                                onToggleReplies={() =>
                                  setOpenThreads((ids) =>
                                    open
                                      ? ids.filter((id) => id !== item.id)
                                      : [...ids, item.id],
                                  )
                                }
                              >
                                <ul className="space-y-3">
                                  {replies.map((reply) => (
                                    <li key={reply.id}>
                                      <CommentBubble
                                        small
                                        tone="bg-paper"
                                        name={reply.userName}
                                        avatarUrl={reply.userAvatarUrl}
                                        createdAt={reply.createdAt}
                                        body={reply.body}
                                        highlight={reply.accepted}
                                        tags={
                                          reply.accepted && (
                                            <CommunityTag tone="bg-acid">
                                              <Check
                                                size={11}
                                                strokeWidth={3}
                                                aria-hidden="true"
                                              />
                                              Respuesta aceptada
                                            </CommunityTag>
                                          )
                                        }
                                        actions={
                                          <>
                                            <MetaAction
                                              onClick={() => replyTo(item.id)}
                                            >
                                              Responder
                                            </MetaAction>
                                            {item.userName === user.name &&
                                              !reply.accepted && (
                                                <MetaAction
                                                  onClick={async () => {
                                                    await apiFetch(
                                                      `/api/community/comments/${reply.id}/accepted`,
                                                      { method: "PATCH" },
                                                    );
                                                    await loadComments();
                                                  }}
                                                >
                                                  Marcar como respuesta
                                                </MetaAction>
                                              )}
                                          </>
                                        }
                                      />
                                    </li>
                                  ))}
                                </ul>
                              </CommentBubble>
                            </li>
                          );
                        })}
                      </ul>
                      {roots.length > COMMENT_PREVIEW && (
                        <ShowAllButton
                          expanded={allComments}
                          hidden={roots.length - COMMENT_PREVIEW}
                          label="Ver todos los comentarios"
                          onToggle={() => setAllComments((value) => !value)}
                        />
                      )}
                    </>
                  );
                })()
              )}
            </section>
          )}
          {(managedCourse?.forum?.enabled ?? true) && catalogCourse?.id && (
            <section
              id="foro-curso"
              className="border-ink bg-paper border-surface mt-7 scroll-mt-24 p-5 sm:p-7"
            >
              <CourseForum courseId={catalogCourse.id} />
            </section>
          )}
        </div>
        {/* Course content column, sticky on wide screens with its own scroll:
            the progress card and the modules move together, scrolling over
            it moves only this column, and the padding leaves room for card
            shadows and hover lifts so nothing looks cut off. */}
        <div className="scroll-fade flex flex-col gap-5 lg:sticky lg:top-[5.75rem] lg:-mx-3 lg:max-h-[calc(100dvh-7.5rem)] lg:overflow-y-auto lg:overscroll-contain lg:px-3 lg:pb-16 lg:pt-2">
          <aside className="border-ink bg-bone border-surface shadow-brutal shrink-0 overflow-hidden">
            <WindowBar tone="grape" title={managedCourse?.title} />
            <div className="border-ink bg-paper border-b-(length:--border-surface) p-5">
              <div className="flex items-center justify-between">
                <p className="font-mono text-xs font-bold uppercase tracking-widest">
                  Contenido del curso
                </p>
                <span className="font-mono text-xs font-bold">
                  {courseProgress}%
                </span>
              </div>
              <div className="bg-ink/15 mt-3 h-2 overflow-hidden">
                <div
                  className="bg-ink h-full transition-[width] duration-500"
                  style={{ width: `${courseProgress}%` }}
                />
              </div>
              <p className="text-ink-soft mt-2 text-xs">
                {completedCount} de {courseLessonCount} clases completadas
              </p>
              {courseProgress === 100 && managedCourse && (
                <form
                  className="border-ink/20 mt-4 border-t pt-4"
                  onSubmit={async (event) => {
                    event.preventDefault();
                    await apiFetch(
                      `/api/education/courses/${managedCourse.id}/review`,
                      {
                        method: "PUT",
                        body: JSON.stringify({
                          rating: reviewRating,
                          body: reviewBody,
                        }),
                      },
                    );
                    setReviewSaved(true);
                  }}
                >
                  <label className="font-mono text-xs font-bold uppercase">
                    Tu valoración
                    <Select
                      value={reviewRating}
                      onChange={(event) =>
                        setReviewRating(Number(event.target.value))
                      }
                      className={cn(inputClass, "ml-2 mt-0 px-2 py-1")}
                    >
                      {[5, 4, 3, 2, 1].map((rating) => (
                        <option key={rating} value={rating}>
                          {rating}/5
                        </option>
                      ))}
                    </Select>
                  </label>
                  <textarea
                    value={reviewBody}
                    onChange={(event) => setReviewBody(event.target.value)}
                    maxLength={3000}
                    placeholder="¿Qué te pareció el curso?"
                    className={cn(inputClass, "mt-2 w-full p-2 text-xs")}
                  />
                  <button
                    className={`${btnAccent} mt-2 min-h-9 px-3 py-2 text-xs`}
                  >
                    {reviewSaved ? "VALORACIÓN GUARDADA" : "GUARDAR VALORACIÓN"}
                  </button>
                </form>
              )}
            </div>
          </aside>
          {/* One card per module: the header is the button, its lessons open
              inside that card, and cards stack with a gap instead of sharing
              one long list. */}
          <div className="flex shrink-0 flex-col gap-4">
            {modules.map((module) => {
              const open = expandedModules.includes(module.number);
              const minutes = module.lessons.reduce(
                (total, lesson) => total + (lesson.durationMinutes ?? 0),
                0,
              );
              const panelId = `module-panel-${module.number}`;
              return (
                <section
                  key={module.number}
                  className={`border-ink bg-bone border-surface shrink-0 overflow-hidden transition-[box-shadow,translate] duration-150 ${open ? "shadow-brutal-lg" : "shadow-brutal hover:shadow-brutal-lg hover:-translate-y-0.5"}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleModule(module.number)}
                    aria-expanded={open}
                    aria-controls={panelId}
                    className={`flex min-h-11 w-full cursor-pointer items-center gap-3 p-4 text-left transition-colors ${open ? "bg-lemon border-ink border-b-(length:--border-surface)" : "hover:bg-paper"}`}
                  >
                    <span className="border-ink bg-bone border-chip font-display grid h-9 w-9 shrink-0 place-items-center text-sm font-extrabold">
                      {module.number}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="text-ink-soft block font-mono text-xs font-bold">
                        {module.lessons.length} clases · {minutes} min ·{" "}
                        {module.progress}
                      </span>
                      <span className="font-display block truncate text-base font-extrabold">
                        {module.title}
                      </span>
                    </span>
                    {module.locked && (
                      <LockKeyhole
                        className="text-ink-soft shrink-0"
                        size={15}
                      />
                    )}
                    <ChevronRight
                      aria-hidden="true"
                      size={18}
                      className={`shrink-0 transition-transform duration-200 ${open ? "rotate-90" : ""}`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {open && !!module.lessons.length && (
                      <motion.div
                        id={panelId}
                        key="lessons"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22, ease: "easeOut" }}
                        className="overflow-hidden"
                      >
                        {module.lessons.map((lesson) => {
                          const active = lesson.id === activeLesson;
                          const done = completedLessons.includes(lesson.id);
                          return (
                            <button
                              key={lesson.id}
                              onClick={() => setActiveLesson(lesson.id)}
                              aria-current={active ? "true" : undefined}
                              className={`group flex min-h-11 w-full cursor-pointer items-center gap-3 border-l-4 py-2.5 pl-4 pr-4 text-left transition-colors ${active ? "border-acid bg-ink text-paper" : "hover:bg-paper border-transparent"}`}
                            >
                              {/* Lesson thumbnail with the play (or done)
                                    badge on top; without an image the frame
                                    falls back to ink so the row keeps its
                                    rhythm. The badge stays small and
                                    translucent so the title card in the
                                    thumbnail remains readable, and sits at
                                    54% to line up with the card's text. */}
                              <span
                                className={`border-chip relative block aspect-video w-24 shrink-0 overflow-hidden ${active ? "border-acid" : "border-ink"} bg-ink`}
                              >
                                {lesson.thumbnailUrl && (
                                  <Photo
                                    src={lesson.thumbnailUrl}
                                    alt=""
                                    loading="lazy"
                                    className="absolute inset-0 h-full w-full object-cover"
                                  />
                                )}
                                <span
                                  className={`border-ink border-chip absolute left-1/2 top-[54%] grid h-6 w-6 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full opacity-60 transition-[transform,opacity] duration-200 group-hover:scale-110 group-hover:opacity-100 ${done ? "bg-acid" : "bg-paper"} text-ink`}
                                >
                                  {done ? (
                                    <Check size={12} strokeWidth={3} />
                                  ) : (
                                    <Play
                                      size={10}
                                      fill="currentColor"
                                      className="ml-0.5"
                                    />
                                  )}
                                </span>
                              </span>
                              {/* Title in the display face, two lines before
                                  it clips; kind and length as a small mono
                                  label underneath. */}
                              <span className="min-w-0 flex-1">
                                <span className="font-display line-clamp-2 block text-[15px] font-bold leading-snug">
                                  {lesson.title}
                                </span>
                                <span
                                  className={`mt-1 flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.06em] ${active ? "text-paper/70" : "text-ink-soft"}`}
                                >
                                  {lesson.kind === "article"
                                    ? "Lectura"
                                    : lesson.kind === "quiz"
                                      ? "Quiz"
                                      : lesson.kind === "project"
                                        ? "Proyecto"
                                        : "Video"}
                                  <span aria-hidden="true">·</span>
                                  <span className="font-normal normal-case tabular-nums tracking-normal">
                                    {lesson.time}
                                  </span>
                                  {done && (
                                    <>
                                      <span aria-hidden="true">·</span>
                                      <span
                                        className={
                                          active ? "text-acid" : "text-ink"
                                        }
                                      >
                                        Vista
                                      </span>
                                    </>
                                  )}
                                </span>
                              </span>
                              {lesson.locked && (
                                <LockKeyhole
                                  className="text-ink-soft shrink-0"
                                  size={14}
                                />
                              )}
                            </button>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </section>
              );
            })}
          </div>
        </div>
      </motion.div>
    </div>
  );

  const overallProgress = courses.length
    ? Math.round(
        courses.reduce((total, course) => total + course.progress, 0) /
          courses.length,
      )
    : 0;

  // In admin the panel supplies its own left nav, so drop the student sidebar.
  const adminMode = current.startsWith("/admin") && Boolean(administrator);
  const content =
    current.startsWith("/admin") && !administrator ? (
      <div className="border-ink bg-bubble border-surface p-8 text-center">
        <ShieldCheck className="mx-auto" size={44} />
        <h1 className="font-display mt-4 text-3xl font-extrabold">
          Acceso restringido
        </h1>
        <p className="text-ink-soft mt-2">
          Tu cuenta no tiene permisos de administración.
        </p>
      </div>
    ) : current.startsWith("/admin") ? (
      <AdminPanel path={current} />
    ) : current === "/paths" || current.startsWith("/paths/") ? (
      // Learning paths out of the MVP (UNB2-170): show a notice if hit directly.
      <div className="border-ink bg-bone border-surface p-8 text-center">
        <strong className="font-display text-2xl">Rutas de aprendizaje</strong>
        <p className="text-ink-soft mt-2">
          Todavía no están disponibles. Las estamos preparando — muy pronto vas
          a poder seguir rutas guiadas.
        </p>
      </div>
    ) : current === "/events" ? (
      <LearningHub section="events" />
    ) : current === "/notifications" ? (
      <LearningHub section="notifications" />
    ) : current === "/teams" ? (
      <LearningHub section="teams" />
    ) : current === "/coach" ? (
      // Coach out of the MVP (UNB2-171): if someone hits /coach directly, show a
      // notice instead of the coach.
      <div className="border-ink bg-bone border-surface p-8 text-center">
        <strong className="font-display text-2xl">Coach de aprendizaje</strong>
        <p className="text-ink-soft mt-2">
          Todavía no está disponible. Lo estamos trabajando — muy pronto vas a
          tener tu coach de IA.
        </p>
      </div>
    ) : playerOpen && catalogLoading ? (
      <PlayerSkeleton />
    ) : playerOpen && !managedCourse ? (
      <div className="border-ink bg-bone border-surface p-8 text-center">
        <ShieldCheck className="mx-auto" size={44} />
        <h1 className="font-display mt-4 text-3xl font-extrabold">
          {catalogCourse
            ? "No tienes acceso a este curso"
            : "Curso no encontrado"}
        </h1>
        <p className="text-ink-soft mx-auto mt-2 max-w-md">
          {catalogCourse
            ? "Necesitas comprar el curso para ver las clases."
            : "Revisa el enlace o explora el catálogo de cursos."}
        </p>
        <a
          href={
            catalogCourse
              ? `https://hey404.com/cursos/${courseSlug}`
              : "https://hey404.com/cursos"
          }
          className="border-ink bg-acid font-display shadow-brutal border-chip mt-6 inline-block px-5 py-3 text-sm font-bold"
        >
          {catalogCourse ? "Ver el curso ↗" : "Explorar catálogo ↗"}
        </a>
      </div>
    ) : playerOpen ? (
      // Invoke as a function, not <CoursePlayer />: these helpers are defined
      // inside the render body, so as an element their type identity changes on
      // every render and React remounts the whole subtree — which destroyed and
      // rebuilt the video player ~4x/s (each onTimeUpdate), causing the restart
      // loop. Called as a function the JSX reconciles in place, no remount.
      CoursePlayer()
    ) : current === "/certificates" ? (
      <>
        <PageHeader
          kicker="Logros"
          title="Certificados"
          doodle="flower-pink"
          tone="lemon"
        />
        {progressLoading ? (
          <div className="mt-10">
            <PanelSkeleton cards={2} header={false} />
          </div>
        ) : certificates.length ? (
          <div className="mt-10 grid gap-5 xl:grid-cols-2">
            {certificates.map((certificate) => {
              const issued = new Date(certificate.issuedAt);
              // Public verification page (same origin as the app).
              const verifyUrl = `${window.location.origin}/verify/${certificate.verificationCode}`;
              // LinkedIn "Add to profile" prefilled with this certification.
              const linkedInUrl = `https://www.linkedin.com/profile/add?${new URLSearchParams(
                {
                  startTask: "CERTIFICATION_NAME",
                  name: certificate.courseTitle,
                  organizationName: "hey404",
                  issueYear: String(issued.getFullYear()),
                  issueMonth: String(issued.getMonth() + 1),
                  certId: certificate.verificationCode,
                  certUrl: verifyUrl,
                },
              ).toString()}`;
              return (
                <article
                  key={certificate.id}
                  className="border-ink bg-bone shadow-brutal border-surface p-6"
                >
                  <Award size={38} />
                  <p className="mt-5 font-mono text-xs font-bold uppercase tracking-widest">
                    Curso completado
                  </p>
                  <h2 className="font-display mt-2 text-2xl font-extrabold">
                    {certificate.courseTitle}
                  </h2>
                  <p className="text-ink-soft mt-2 text-sm">
                    Emitido el{" "}
                    {new Date(certificate.issuedAt).toLocaleDateString("es-AR")}
                  </p>
                  <p className="text-ink-soft mt-1 font-mono text-xs">
                    CÓDIGO {certificate.verificationCode}
                  </p>
                  <button
                    onClick={() =>
                      downloadAuthenticatedFile(
                        `/api/learning/certificates/${certificate.id}/pdf`,
                        `certificado-${certificate.courseTitle.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`,
                      )
                    }
                    className={`${btnPrimary} mt-6 w-full gap-2 px-4 py-3`}
                  >
                    <Download size={18} /> Descargar PDF
                  </button>
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <a
                      href={linkedInUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${btnSecondary} justify-center gap-2 px-4 py-2.5 text-sm`}
                    >
                      Compartir en LinkedIn ↗
                    </a>
                    <a
                      href={verifyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${btnSecondary} justify-center gap-2 px-4 py-2.5 text-sm`}
                    >
                      <ShieldCheck size={16} /> Ver verificación
                    </a>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <CertificateProgress
            studentName={user.name}
            course={
              currentManaged
                ? {
                    title: currentManaged.title,
                    completed: currentLessons.filter((lesson) =>
                      completedLessons.includes(lesson.id),
                    ).length,
                    total: currentLessons.length,
                  }
                : null
            }
            action={
              resumeLesson
                ? {
                    label: lastViewed ? "Ir a la clase" : "Empezar la clase",
                    onClick: () =>
                      go(
                        `/courses/${currentCourseSlug}?lesson=${resumeLesson.id}${resumeSeconds ? `&t=${resumeSeconds}` : ""}`,
                      ),
                  }
                : currentCourseSlug
                  ? {
                      label: "Ir al curso",
                      onClick: () => go(`/courses/${currentCourseSlug}`),
                    }
                  : {
                      label: "Conocer el curso",
                      onClick: () =>
                        window.location.assign(
                          onSaleCourse
                            ? `https://hey404.com/cursos/${onSaleCourse.slug}`
                            : "https://hey404.com",
                        ),
                    }
            }
          />
        )}
      </>
    ) : current === "/guardadas" ? (
      <SavedLessons />
    ) : current === "/intereses" ? (
      <InterestsForm />
    ) : current === "/settings" ? (
      <AccountSettings user={user} />
    ) : (
      <>
        <PageHeader
          kicker="Overview"
          title={
            <>
              Hola, <Marker>{user.name.split(" ")[0]}</Marker>.
            </>
          }
          description="Este es el estado de tu aprendizaje."
          doodle="face-lime"
          tone="lemon"
        />
        <div className="mt-10 grid items-stretch gap-6 sm:grid-cols-2">
          <button
            type="button"
            onClick={() =>
              go(
                currentCourseSlug
                  ? `/courses/${currentCourseSlug}`
                  : "/overview",
              )
            }
            className="home-step bg-bubble rounded-[3px_24px_7px_8px] text-left"
          >
            <span className="flex items-center justify-between gap-4">
              <strong className="home-step-number">{overallProgress}%</strong>
              <span className="home-step-symbol" aria-hidden="true">
                <Doodle name="key-cream" tilt={0} className="w-8" />
              </span>
            </span>
            <span className="font-display mt-6 block text-2xl font-extrabold">
              Avance total
            </span>
            <span className="border-ink bg-paper border-chip mt-4 block h-3 overflow-hidden rounded-full">
              <span
                className="bg-ink block h-full"
                style={{ width: `${overallProgress}%` }}
              />
            </span>
            <span className="home-step-arrow" aria-hidden="true">
              ↗
            </span>
          </button>
          <button
            type="button"
            onClick={() => go("/certificates")}
            className="home-step bg-lemon rounded-[22px_4px_20px_3px] text-left"
          >
            <span className="flex items-center justify-between gap-4">
              <strong className="home-step-number">
                {certificates.length}
              </strong>
              <span className="home-step-symbol" aria-hidden="true">
                <Doodle name="flower-pink" tilt={0} className="w-8" />
              </span>
            </span>
            <span className="font-display mt-6 block text-2xl font-extrabold">
              {certificates.length === 1 ? "Certificado" : "Certificados"}
            </span>
            <span className="mt-2 block text-sm">
              {certificates.length
                ? "Listo para compartir en LinkedIn."
                : "Se emite cuando completes el curso."}
            </span>
            <span className="home-step-arrow" aria-hidden="true">
              ↗
            </span>
          </button>
        </div>
        {resumeLesson ? (
          <ContinueWatching
            courseTitle={currentManaged?.title ?? "Tu curso"}
            lesson={resumeLesson}
            positionSeconds={resumeSeconds}
            mode={
              !lastViewed
                ? "first"
                : resumeIndex === lastIndex
                  ? "resume"
                  : "next"
            }
            onPlay={(lessonId, startAt) =>
              go(
                `/courses/${currentCourseSlug}?lesson=${lessonId}${startAt ? `&t=${startAt}` : ""}`,
              )
            }
          />
        ) : catalogLoading || continueQuery.isPending ? (
          <ContinueSkeleton />
        ) : catalogError ? (
          // Failing to load must not read as "you have no course".
          <div
            role="alert"
            className="border-ink border-l-danger bg-paper border-surface mt-16 border-l-8 p-6"
          >
            <strong className="font-display text-xl">
              No pudimos cargar tu curso.
            </strong>
            <p className="text-ink-soft mt-2 text-sm">
              Revisa tu conexión y vuelve a intentarlo.
            </p>
            <button
              type="button"
              onClick={() => catalogQuery.refetch()}
              className={`${btnSecondary} mt-4 px-4 py-2`}
            >
              Reintentar
            </button>
          </div>
        ) : (
          <StartCourse
            course={
              currentManaged
                ? {
                    title: currentManaged.title,
                    description: currentManaged.description,
                    lessons: currentLessons.length,
                    minutes: currentLessons.reduce(
                      (total, lesson) => total + lesson.durationMinutes,
                      0,
                    ),
                  }
                : null
            }
            suggestion={
              onSaleCourse
                ? {
                    title: onSaleCourse.title,
                    href: `https://hey404.com/cursos/${onSaleCourse.slug}`,
                  }
                : null
            }
            onStart={() =>
              currentCourseSlug
                ? go(`/courses/${currentCourseSlug}`)
                : window.location.assign(
                    onSaleCourse
                      ? `https://hey404.com/cursos/${onSaleCourse.slug}`
                      : "https://hey404.com",
                  )
            }
          />
        )}
        <ComingSoon />
      </>
    );
  // Without any course the platform has a single thing to offer: buying it.
  // Admins keep the full platform. If the catalog failed to load, fall
  // through rather than lock a student out.
  if (!administrator && catalogQuery.isSuccess && !hasEnrolledCourse)
    return (
      <BuyCourseGate
        course={onSaleCourse}
        onSignOut={async () => {
          await authClient.signOut();
          clearAuthToken();
          queryClient.clear();
          window.location.assign("/login");
        }}
      />
    );
  return (
    <div className="bg-paper text-ink min-h-dvh">
      <header className="border-ink bg-paper border-b-(length:--border-surface) sticky top-0 z-30 flex h-16 items-center px-4 sm:px-5">
        <button
          className="mr-3 md:hidden"
          onClick={() => setMenuOpen(true)}
          aria-label="Abrir navegación"
        >
          <Menu />
        </button>
        <button onClick={() => go("/overview")} aria-label="Ir al overview">
          <img src="/logo-final.svg" alt="hey404" className="h-10 w-auto" />
        </button>
        <div className="ml-auto flex items-center gap-3">
          {visibility?.notifications && (
            <NotificationsBell
              unread={counts?.unreadNotifications ?? 0}
              go={go}
            />
          )}
          {playerOpen && (
            <button
              type="button"
              onClick={() => {
                // Ask in the course forum if there is one; otherwise the button
                // must still do something, so fall back to support (UNB2-114).
                const forum = document.getElementById("foro-curso");
                if (forum)
                  forum.scrollIntoView({ behavior: "smooth", block: "start" });
                else
                  window.location.href =
                    "mailto:hola@hey404.com?subject=Ayuda%20con%20hey404";
              }}
              className="text-ink-soft hidden cursor-pointer items-center gap-2 font-mono text-xs font-bold uppercase hover:underline lg:flex"
            >
              <MessageCircle size={14} /> ¿Necesitas ayuda?
            </button>
          )}
          <button
            onClick={async () => {
              await authClient.signOut();
              clearAuthToken();
              queryClient.clear();
              window.location.assign("/login");
            }}
            className="border-ink bg-bone shadow-brutal border-chip p-2"
            aria-label="Cerrar sesión"
          >
            <LogOut size={17} />
          </button>
        </div>
      </header>
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="fixed inset-0 z-40 overscroll-contain md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ScrollLock />
            <motion.button
              className="bg-ink/40 absolute inset-0"
              onClick={() => setMenuOpen(false)}
              aria-label="Cerrar navegación"
            />
            <motion.div
              className="relative h-full w-[min(85vw,300px)]"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 350, damping: 34 }}
            >
              <button
                className="border-ink bg-bubble border-chip absolute right-3 top-3 z-10 p-1"
                onClick={() => setMenuOpen(false)}
                aria-label="Cerrar navegación"
              >
                <X />
              </button>
              {adminMode ? (
                <nav className="bg-ink text-paper flex h-full flex-col gap-1 overflow-y-auto p-4 pt-16">
                  <p className="px-3 pb-2 font-mono text-xs font-bold uppercase opacity-50">
                    Administración
                  </p>
                  {adminSections
                    .filter((item) => adminPerms.includes(item.id))
                    .map((item) => {
                      const Icon = item.icon;
                      const active = adminSectionFromPath(current) === item.id;
                      return (
                        <button
                          key={item.id}
                          onClick={() => {
                            window.history.pushState(
                              {},
                              "",
                              adminSectionPaths[item.id],
                            );
                            window.dispatchEvent(new PopStateEvent("popstate"));
                            setMenuOpen(false);
                          }}
                          aria-current={active ? "page" : undefined}
                          className={`rounded-control flex min-h-11 items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors ${active ? "bg-acid text-ink shadow-nav" : "text-paper/90 hover:bg-bubble hover:text-ink hover:shadow-nav"}`}
                        >
                          <Icon size={17} aria-hidden="true" /> {item.label}
                        </button>
                      );
                    })}
                </nav>
              ) : (
                Sidebar()
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <div
        className={`grid w-full transition-[grid-template-columns] duration-300 ease-[cubic-bezier(0.22,0.8,0.25,1)] ${adminMode ? "" : sidebarCollapsed ? "md:grid-cols-[76px_minmax(0,1fr)]" : "md:grid-cols-[256px_minmax(0,1fr)]"}`}
      >
        {!adminMode && (
          <div className="sticky top-16 hidden h-[calc(100dvh-64px)] self-start md:block">
            {Sidebar(true)}
          </div>
        )}
        {/* No page transition: the old one faded the content to blank
            before the next page mounted (~200 ms on every click), which read
            as the whole layout reloading. The content now swaps in place
            while the sidebar and header stay still. The key still resets
            each page's state; the admin keeps one key so its panel (and
            sidebar) stay mounted across sections. */}
        <main
          key={adminMode ? "/admin" : current}
          className={`platform-grid min-w-0 ${adminMode ? "" : `p-5 sm:p-8 ${playerOpen ? "lg:p-7" : "lg:p-12"}`}`}
        >
          {content}
        </main>
      </div>
    </div>
  );
}

// Full-screen, non-dismissible lock for suspended accounts. The user can enter
// and see the app behind it, but this overlay swallows every interaction; the
// only way out is signing out.
function BannedOverlay({ reason }: { reason: string | null }) {
  return (
    <div
      className="bg-ink/70 fixed inset-0 z-[100] grid place-items-center overscroll-contain p-6 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <ScrollLock />
      <div className="border-ink bg-paper shadow-brutal border-chip w-full max-w-md p-8 text-center">
        <div className="border-ink bg-bubble border-chip mx-auto grid h-14 w-14 place-items-center">
          <LockKeyhole size={26} />
        </div>
        <h2 className="font-display mt-5 text-2xl font-extrabold">
          Cuenta suspendida
        </h2>
        <p className="text-ink-soft mt-3">
          Tu acceso a hey404 fue suspendido, por lo que no puedes usar la
          plataforma en este momento.
        </p>
        {reason && (
          <p className="border-ink bg-bone border-chip mt-4 p-3 text-sm">
            <span className="font-bold">Motivo:</span> {reason}
          </p>
        )}
        <p className="text-ink-soft mt-4 text-sm">
          Si crees que es un error, escríbenos a{" "}
          <a className="font-bold underline" href="mailto:hola@hey404.com">
            hola@hey404.com
          </a>
          .
        </p>
        <button
          onClick={async () => {
            await authClient.signOut();
            clearAuthToken();
            queryClient.clear();
            window.location.assign("/login");
          }}
          className={`${btnPrimary} mt-6 w-full px-4 py-3`}
        >
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}

type VerifiedCertificate = {
  recipientName: string;
  courseTitle: string;
  issuedAt: string;
  verificationCode: string;
};

// Public, unauthenticated page a certificate's QR/link points to. Fetches the
// public verify endpoint and renders the result on-brand — a 404 means the code
// is not a real certificate (shown as "no verificado", not an error).
function VerifyCertificate({ code }: { code: string }) {
  const [status, setStatus] = useState<
    "loading" | "valid" | "invalid" | "error"
  >("loading");
  const [certificate, setCertificate] = useState<VerifiedCertificate | null>(
    null,
  );
  useEffect(() => {
    document.title = "Verificar certificado — hey404";
    let active = true;
    fetch(`${apiUrl}/api/certificates/verify/${encodeURIComponent(code)}`)
      .then(async (response) => {
        if (!active) return;
        if (response.ok) {
          const data = (await response.json()) as {
            certificate: VerifiedCertificate;
          };
          setCertificate(data.certificate);
          setStatus("valid");
        } else if (response.status === 404) {
          setStatus("invalid");
        } else {
          setStatus("error");
        }
      })
      .catch(() => {
        if (active) setStatus("error");
      });
    return () => {
      active = false;
    };
  }, [code]);
  return (
    <main className="platform-grid bg-paper grid min-h-dvh place-items-center px-5 py-10">
      <div className="w-full max-w-xl">
        <a href="https://hey404.com" className="mb-8 inline-block">
          <img src="/logo-final.svg" alt="hey404" className="h-11 w-auto" />
        </a>
        <section className="border-ink bg-bone shadow-brutal-xl border-surface p-7 sm:p-10">
          {status === "loading" && (
            <p className="text-ink-soft font-mono text-sm" role="status">
              Verificando certificado…
            </p>
          )}
          {status === "valid" && certificate && (
            <>
              <span className="border-ink bg-acid border-surface inline-flex items-center gap-2 px-3 py-1.5 font-mono text-xs font-bold uppercase tracking-widest">
                <ShieldCheck size={15} /> Certificado verificado
              </span>
              <p className="text-ink-soft mt-7 font-mono text-xs font-bold uppercase tracking-widest">
                Otorgado a
              </p>
              <h1 className="font-display mt-2 text-4xl font-extrabold leading-[1.05] sm:text-5xl">
                {certificate.recipientName}
              </h1>
              <p className="text-ink-soft mt-6 text-sm">
                por completar el curso
              </p>
              <p className="font-display mt-1 text-2xl font-extrabold">
                {certificate.courseTitle}
              </p>
              <div className="border-ink border-t-(length:--border-surface) mt-8 flex flex-wrap gap-x-10 gap-y-3 pt-6 font-mono text-xs">
                <span>
                  <span className="text-ink-soft block text-xs uppercase tracking-widest">
                    Emitido
                  </span>
                  {new Date(certificate.issuedAt).toLocaleDateString("es-AR", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
                <span>
                  <span className="text-ink-soft block text-xs uppercase tracking-widest">
                    Código
                  </span>
                  {certificate.verificationCode}
                </span>
              </div>
            </>
          )}
          {status === "invalid" && (
            <>
              <span className="border-ink bg-bubble border-surface inline-flex items-center gap-2 px-3 py-1.5 font-mono text-xs font-bold uppercase tracking-widest">
                <AlertCircle size={15} /> No verificado
              </span>
              <h1 className="font-display mt-7 text-3xl font-extrabold sm:text-4xl">
                No encontramos este certificado
              </h1>
              <p className="text-ink-soft mt-3 text-sm">
                El código{" "}
                <span className="text-ink font-mono font-bold">{code}</span> no
                corresponde a ningún certificado emitido por hey404. Revisa que
                esté completo y bien escrito.
              </p>
            </>
          )}
          {status === "error" && (
            <>
              <h1 className="font-display text-3xl font-extrabold">
                No pudimos verificar ahora
              </h1>
              <p className="text-ink-soft mt-3 text-sm">
                Hubo un problema de conexión. Prueba de nuevo en un momento.
              </p>
            </>
          )}
        </section>
        <p className="text-ink-soft mt-6 text-center font-mono text-xs">
          Verificación de certificados de{" "}
          <a href="https://hey404.com" className="font-bold underline">
            hey404.com
          </a>
        </p>
      </div>
    </main>
  );
}

// Every top-level path the platform actually serves. Auth/checkout/verify routes
// are matched explicitly in App() before this; these are the ones the Dashboard
// renders internally. Anything else is a 404.
const KNOWN_APP_PATHS = new Set([
  "/",
  "/overview",
  "/courses",
  "/guardadas",
  "/certificates",
  "/settings",
  "/paths",
  "/events",
  "/notifications",
  "/teams",
  "/coach",
]);
const isKnownAppPath = (path: string) =>
  KNOWN_APP_PATHS.has(path) ||
  path.startsWith("/courses/") ||
  path.startsWith("/paths/") ||
  path.startsWith("/admin");

// Branded catch-all for unknown platform paths (UNB2-84).
function NotFound() {
  useEffect(() => {
    document.title = "Página no encontrada — hey404";
  }, []);
  return (
    <main className="platform-grid bg-paper grid min-h-dvh place-items-center px-5 py-10">
      <div className="w-full max-w-lg text-center">
        <a href="/overview" className="mb-8 inline-block">
          <img src="/logo-final.svg" alt="hey404" className="h-11 w-auto" />
        </a>
        <p className="font-display text-ink text-[7rem] font-extrabold leading-none">
          404
        </p>
        <h1 className="font-display mt-2 text-3xl font-extrabold">
          Esta página no existe
        </h1>
        <p className="text-ink-soft mt-3">
          El enlace es incorrecto o la página se movió.
        </p>
        <a
          href="/overview"
          className={`${btnPrimary} mt-8 inline-flex px-6 py-3`}
        >
          Ir a mi inicio
        </a>
      </div>
    </main>
  );
}

function App() {
  const [path, setPath] = useState(window.location.pathname);
  const { data: session, isPending: sessionPending } = authClient.useSession();
  // Only the first session check holds the page. better-auth re-checks the
  // session when the tab regains focus, flipping isPending back to true while
  // logged out; gating on that would swap the page for the skeleton and wipe
  // whatever was being typed (e.g. the activation code after a purchase).
  const sessionSettled = useRef(false);
  if (!sessionPending) sessionSettled.current = true;
  const isPending = sessionPending && !sessionSettled.current;
  const previousUserId = useRef<string | null>(null);
  const [administrator, setAdministrator] = useState(false);
  // Starts true on every path: the nav's "Administración" entry depends on it,
  // and bootPending holds the skeleton until it settles.
  const [checkingAdministrator, setCheckingAdministrator] = useState(true);
  useEffect(() => {
    const nextUserId = session?.user?.id ?? null;
    if (previousUserId.current && previousUserId.current !== nextUserId) {
      queryClient.clear();
      resetAnalytics();
    }
    previousUserId.current = nextUserId;
    if (session?.user)
      identifyStudent({
        id: session.user.id,
        email: session.user.email,
        name: session.user.name,
      });
    // The user object changes identity on every session refetch; the id is
    // what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);
  useEffect(() => {
    const update = () => setPath(window.location.pathname);
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);
  useEffect(() => {
    if (!session?.user) {
      setAdministrator(false);
      setCheckingAdministrator(false);
      return;
    }
    setCheckingAdministrator(true);
    apiFetch("/api/admin/me")
      .then(() => setAdministrator(true))
      .catch(() => setAdministrator(false))
      .finally(() => setCheckingAdministrator(false));
    // Key on the user id (primitive), NOT the session.user object: better-auth
    // recreates that object on every window-focus revalidation, and depending on
    // the object ref would re-run this (flip checkingAdministrator → unmount the
    // whole admin panel → lose the editor selection). Re-check only when the
    // signed-in user actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- id-only dep is intentional (see comment above)
  }, [session?.user?.id]);
  useEffect(() => {
    const titles: Record<string, string> = {
      "/": "Overview — hey404",
      "/overview": "Overview — hey404",
      "/courses": "Mis cursos — hey404",
      "/guardadas": "Clases guardadas — hey404",
      "/certificates": "Certificados — hey404",
      "/settings": "Configuración — hey404",
      "/paths": "Rutas de aprendizaje — hey404",
      "/events": "Eventos y clases en vivo — hey404",
      "/teams": "Mis equipos — hey404",
      "/coach": "Coach de aprendizaje — hey404",
      "/signup": "Crear cuenta — hey404",
      "/login": "Ingresar — hey404",
      "/forgot-password": "Recuperar contraseña — hey404",
      "/reset-password": "Restablecer contraseña — hey404",
      "/checkout": "Checkout — hey404",
      "/checkout/success": "Compra confirmada — hey404",
      "/checkout/cancel": "Pago cancelado — hey404",
      "/admin": "Administración — hey404",
    };
    document.title = path.startsWith("/admin/")
      ? "Administración — hey404"
      : path.startsWith("/courses/")
        ? "Tablas, campos y relaciones — hey404"
        : (titles[path] ?? "Mi aprendizaje — hey404");
  }, [path]);
  const sessionUser = session?.user
    ? {
        ...session.user,
        name:
          session.user.name?.trim() ||
          session.user.email?.split("@")[0] ||
          "Estudiante",
        email: session.user.email || "",
      }
    : null;
  // Hold the shell skeleton until the dashboard's first screen has its data
  // (see catalogQueryOptions), so it appears in one piece. Errors count as
  // settled: each section already renders its own error state.
  const bootQueries = useQueries({
    queries: [
      catalogQueryOptions,
      continueQueryOptions,
      navigationQueryOptions,
      learningQueryOptions,
    ].map((options) => ({ ...options, enabled: Boolean(session?.user) })),
  });
  const bootPending =
    Boolean(sessionUser) &&
    !path.startsWith("/checkout") &&
    (checkingAdministrator || bootQueries.some((query) => query.isPending));
  // Public certificate verification — render before the session gate so it works
  // logged out and doesn't flash the loading skeleton while the session resolves.
  if (path.startsWith("/verify/")) {
    const code = path.slice("/verify/".length).replace(/\/+$/, "");
    return <VerifyCertificate code={code} />;
  }
  if (
    isPending ||
    bootPending ||
    (path.startsWith("/admin") && checkingAdministrator)
  )
    return path === "/checkout" ? (
      <CheckoutLayout
        sessionPending
        backHref={checkoutLandingUrl}
        summary={
          <p className="checkout-status" role="status">
            {checkoutCopy.quotePending}
          </p>
        }
      >
        {null}
      </CheckoutLayout>
    ) : (
      // The first paint already looks like the page being opened, so the
      // next stage (that page's own loading state) doesn't swap layouts.
      <AppShellSkeleton>
        {path === "/settings" ? <SettingsLoading /> : undefined}
      </AppShellSkeleton>
    );
  const bannedUser = sessionUser as
    | (typeof sessionUser & { banned?: boolean; banReason?: string | null })
    | null;
  if (bannedUser?.banned)
    return (
      <>
        <Dashboard user={bannedUser} administrator={false} />
        <BannedOverlay reason={bannedUser.banReason ?? null} />
      </>
    );
  if (path === "/checkout")
    return (
      <Checkout
        key={session?.user?.id ?? "guest"}
        user={sessionUser ?? undefined}
      />
    );
  if (path === "/checkout/success")
    return <CheckoutSuccess user={sessionUser ?? undefined} />;
  if (path === "/checkout/cancel")
    return <CheckoutCancel user={sessionUser ?? undefined} />;
  if (path === "/forgot-password") return <ForgotPassword />;
  if (path === "/reset-password") return <ResetPassword />;
  // Accounts come from buying: signing up is the same checkout, paid with an
  // email (the account is created and activated after the payment).
  if (path === "/signup")
    return sessionUser ? (
      <Dashboard user={sessionUser} administrator={administrator} />
    ) : (
      <SignupToCheckout />
    );
  if (path === "/login")
    return sessionUser ? (
      <Dashboard user={sessionUser} administrator={administrator} />
    ) : (
      <AuthScreen mode="login" />
    );
  if (!isKnownAppPath(path)) return <NotFound />;
  return sessionUser ? (
    <Dashboard user={sessionUser} administrator={administrator} />
  ) : (
    <AuthScreen mode="login" />
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 10 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: { retry: 0 },
  },
});

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <App />
      </ToastProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
